import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  type AuditCheck, type AuditHistoryRun, type AuditResponse, type Ga4Response,
  type IndexingResponse, type KeywordSummary, type LeadRow,
  type PressRelease, type ScResponse, type SiteDetail,
  type SitemapReportResponse,
  pressReleasesApi, sitesApi, healthApi, sitemapApi, psiApi, type PsiRun,
  rankingsApi, refreshQueueApi, type RankRow, type RefreshQueueItem,
  agentsApi, type SiteAgentAction,
  ahrefsApi,
  callsApi, type CallEntry,
  optimizeApi, type DiscoveredKeyword,
} from "../lib/api";
import { HeaderMeta } from "../components/HeaderMeta";
import { AgentAvatar } from "../components/AgentAvatar";
import { SortHeader } from "../components/SortHeader";
import { useSortableTable } from "../hooks/useSortableTable";
import { pageLabel } from "../lib/pageLabel";
import { ExternalSitePanel } from "../components/ExternalSitePanel";

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ["site", id],
    queryFn: () => sitesApi.detail(id!),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });

  const scQ = useQuery({
    queryKey: ["site-sc", id],
    queryFn: () => sitesApi.sc(id!, 30),
    enabled: Boolean(id) && Boolean(q.data?.site.searchConsoleVerified),
    refetchInterval: 5 * 60_000,
  });
  const auditQ = useQuery({
    queryKey: ["site-audit", id],
    queryFn: () => sitesApi.audit(id!),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
  const ga4Q = useQuery({
    queryKey: ["site-ga4", id],
    queryFn: () => sitesApi.ga4(id!, 30),
    enabled: Boolean(id) && Boolean(q.data?.site.ga4MeasurementId),
    refetchInterval: 5 * 60_000,
  });

  if (q.isLoading) return <Loading />;
  if (q.error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
        <strong>Failed to load site.</strong> {String((q.error as Error).message)}
      </div>
    );
  if (!q.data) return null;
  const { site, recentLeads } = q.data;

  return (
    <div className="space-y-6">
      <BackBar />
      <Hero site={site} />
      {site.siteKind === "external_managed" && (
        <ExternalSitePanel
          siteId={site.id}
          domain={site.domain}
          publishUrl={site.externalPublishUrl ?? null}
          publishSecretSet={Boolean(site.externalPublishSecretSet)}
          defaultProbeLocation={site.defaultProbeLocation ?? null}
        />
      )}
      <Stats site={site} />
      <HealthCard siteId={site.id} />
      <SitemapReportCard siteId={site.id} />
      {site.searchConsoleVerified && <SearchConsoleCard data={scQ.data} loading={scQ.isLoading} error={scQ.error as Error | null} />}
      {site.searchConsoleVerified && <IndexingCard siteId={site.id} />}
      <PerformanceCard siteId={site.id} />
      <RankTrackerCard siteId={site.id} domain={site.domain} />
      <OptimizeAttemptsCard siteId={site.id} />
      <DiscoveredKeywordsCard siteId={site.id} />
      <CompetitiveLandscapeCard siteId={site.id} />
      <CallsCard siteId={site.id} />
      {site.searchConsoleVerified && <RefreshQueueCard siteId={site.id} />}
      {site.searchConsoleVerified && <TargetKeywordsCard siteId={site.id} />}
      {site.ga4MeasurementId && <Ga4Card data={ga4Q.data} loading={ga4Q.isLoading} error={ga4Q.error as Error | null} />}
      <AuditCard data={auditQ.data} loading={auditQ.isLoading} error={auditQ.error as Error | null} />
      <AhrefsIssuesCard siteId={site.id} />
      <AuditHistoryCard siteId={site.id} />
      <PressReleaseCard site={site} />
      <ModeFlipCard site={site} />
      <TierFlipCard site={site} />
      <AuthorCard site={site} />
      <BlogPostsCard siteId={site.id} domain={site.domain} />
      <OffPageCard siteId={site.id} />
      <RecentAgentActivityCard siteId={site.id} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentLeads leads={recentLeads} site={site} />
        </div>
        <div className="space-y-6">
          <Metadata site={site} />
        </div>
      </div>
    </div>
  );
}

function BackBar() {
  return (
    <Link to="/sites" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-900">
      <span aria-hidden>←</span> All sites
    </Link>
  );
}

function Hero({ site }: { site: SiteDetail }) {
  return (
    <div className="bg-white rounded-2xl border border-ink-100 p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold mb-1">{site.domain}</h1>
          <div className="flex flex-wrap gap-2 text-sm text-ink-600">
            <span>
              {[site.city, site.state].filter(Boolean).join(", ") || "—"} · {site.niche || "—"}
            </span>
            <span aria-hidden className="text-ink-300">·</span>
            <span className="font-mono text-xs text-ink-500">{site.id.slice(0, 8)}…</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Pill tone={site.mode === "branded_lessee" ? "amber" : "blue"}>
            {site.mode === "branded_lessee" ? "Branded lessee" : "Lead-gen"}
          </Pill>
          <Pill tone={statusTone(site.status)}>{site.status}</Pill>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={`https://${site.domain}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-ink-900 text-white rounded-md hover:bg-ink-800"
        >
          Visit site <span aria-hidden>↗</span>
        </a>
        {site.hostingProjectId && (
          <a
            href={`https://dash.cloudflare.com/?to=/:account/pages/view/${site.hostingProjectId}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-ink-200 text-ink-700 rounded-md hover:bg-ink-50"
          >
            Cloudflare Pages <span aria-hidden>↗</span>
          </a>
        )}
        {site.ga4MeasurementId && (
          <a
            href="https://analytics.google.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-ink-200 text-ink-700 rounded-md hover:bg-ink-50"
          >
            GA4 ({site.ga4MeasurementId}) <span aria-hidden>↗</span>
          </a>
        )}
        {site.searchConsoleVerified && (
          <a
            href={`https://search.google.com/search-console?resource_id=sc-domain:${site.domain}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-ink-200 text-ink-700 rounded-md hover:bg-ink-50"
          >
            Search Console <span aria-hidden>↗</span>
          </a>
        )}
      </div>
    </div>
  );
}

function Stats({ site }: { site: SiteDetail }) {
  const daysLive = site.deployedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(site.deployedAt).getTime()) / 86_400_000))
    : null;
  const daysSinceLast = site.lastLeadAt
    ? Math.max(0, Math.floor((Date.now() - new Date(site.lastLeadAt).getTime()) / 86_400_000))
    : null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Total leads" value={site.totalLeads} />
      <Stat label="Last 7 days" value={site.recentLeads} accent={site.recentLeads > 0} />
      <Stat label="High-quality" value={site.highScoreLeads} accent={site.highScoreLeads > 0} hint="≥50/100, non-spam" />
      <Stat
        label="Days live"
        value={daysLive ?? "—"}
        hint={daysSinceLast !== null ? `last lead: ${daysSinceLast}d ago` : "no leads yet"}
      />
    </div>
  );
}

function Stat({ label, value, hint, accent }: { label: string; value: number | string; hint?: string; accent?: boolean }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`mt-1 text-2xl font-display font-bold tabular-nums ${accent ? "text-accent-600" : "text-ink-900"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-ink-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function RecentLeads({ leads, site }: { leads: LeadRow[]; site: SiteDetail }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<LeadRow>(
    leads,
    {
      received: (l) => l.receivedAt,
      caller: (l) => l.callerName,
      phone: (l) => l.callerPhone,
      score: (l) => l.qualityScore,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-ink-100 flex items-center justify-between">
        <h2 className="font-semibold">Recent leads</h2>
        <Link
          to={`/leads?siteId=${site.id}`}
          className="text-xs text-ink-500 hover:text-ink-900 underline decoration-dotted"
        >
          View all →
        </Link>
      </div>
      {leads.length === 0 ? (
        <div className="p-8 text-center text-ink-500 text-sm">
          No leads yet. They'll appear here within seconds of submission.
        </div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <SortHeader label="Received" sortKey="received" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-2.5" />
              <SortHeader label="Caller" sortKey="caller" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-2.5" />
              <SortHeader label="Phone" sortKey="phone" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-2.5" />
              <SortHeader label="Score" sortKey="score" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.map((l) => (
              <tr key={l.id} className={l.isSpam ? "opacity-50 hover:bg-ink-50/50" : "hover:bg-ink-50/50"}>
                <td className="px-4 py-2.5 text-ink-600 whitespace-nowrap text-xs">{timeAgo(l.receivedAt)}</td>
                <td className="px-4 py-2.5">{l.callerName ?? "—"}</td>
                <td className="px-4 py-2.5">
                  {l.callerPhone ? (
                    <a href={`tel:${l.callerPhone}`} className="text-ink-900 hover:text-accent">
                      {l.callerPhone}
                    </a>
                  ) : <span className="text-ink-400">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <ScoreBadge score={l.qualityScore ?? 0} isSpam={l.isSpam} type={l.type} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function Ga4Card({ data, loading, error }: {
  data: Ga4Response | undefined;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Traffic — GA4 (last 30 days)</h2>
        <HeaderMeta lastRefreshed={data?.dataRefreshedAt} fallback="No data yet" cron="@daily-at-8-utc" />
      </div>
      {loading && <div className="text-ink-500 text-sm">Loading…</div>}
      {error && <div className="text-red-700 text-sm">Failed to load: {String(error.message)}</div>}
      {data && data.totals.totalUsers === 0 && (
        <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
          No GA4 data yet. Run <code className="bg-white px-1 rounded text-xs">rankforge ga4-pull --site &lt;dir&gt;</code> on
          a daily cron once traffic starts arriving. (Or visit the live site once to seed a session, then re-pull.)
        </div>
      )}
      {data && data.totals.totalUsers > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <ScStat label="Users" value={data.totals.totalUsers.toLocaleString()} accent={data.totals.totalUsers > 0} />
            <ScStat label="New users" value={data.totals.newUsers.toLocaleString()} />
            <ScStat label="Sessions" value={data.totals.sessions.toLocaleString()} />
            <ScStat label="Engaged" value={data.totals.engagedSessions.toLocaleString()} />
          </div>

          {data.daily.length > 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <SparkBlock
                label={`Users trend (max ${Math.max(...data.daily.map((d) => d.totalUsers), 1).toLocaleString()})`}
                color="text-accent-600"
                path={spark(data.daily.map((d) => d.totalUsers))}
                W={600} H={60}
              />
              <SparkBlock
                label={`Sessions trend (max ${Math.max(...data.daily.map((d) => d.sessions), 1).toLocaleString()})`}
                color="text-blue-600"
                path={spark(data.daily.map((d) => d.sessions))}
                W={600} H={60}
              />
            </div>
          )}

          {data.topPages.length > 0 && (
            <Ga4TopPagesTable pages={data.topPages.slice(0, 15)} />
          )}
        </>
      )}
    </div>
  );
}

function Ga4TopPagesTable({ pages }: { pages: NonNullable<Ga4Response["topPages"]> }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    pages,
    {
      path: (p) => p.pagePath,
      sessions: (p) => p.sessions,
      users: (p) => p.users,
    },
    { key: "sessions", dir: "desc" },
  );
  return (
    <div>
      <h3 className="text-sm font-semibold text-ink-700 mb-2">Top pages</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <SortHeader label="Path" sortKey="path" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
              <SortHeader label="Sessions" sortKey="sessions" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
              <SortHeader label="Users" sortKey="users" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 pl-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.map((p) => (
              <tr key={p.pagePath}>
                <td className="py-1.5 pr-3 font-mono text-xs text-ink-900">{p.pagePath}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{p.sessions.toLocaleString()}</td>
                <td className="py-1.5 pl-3 text-right tabular-nums">{p.users.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function spark(vals: number[]): string {
  const W = 600;
  const H = 60;
  const max = Math.max(...vals, 1);
  const xStep = vals.length > 1 ? W / (vals.length - 1) : 0;
  return vals
    .map((v, i) => {
      const x = i * xStep;
      const y = H - (v / max) * H;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function AuditCard({ data, loading, error }: {
  data: AuditResponse | undefined;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Audit (PRD Override §9)</h2>
        <HeaderMeta
          lastRefreshed={data?.latest?.ranAt ?? null}
          fallback="Never run"
          cron="@daily-at-8-utc"
          extra={data?.runCount ? `${data.runCount} run${data.runCount === 1 ? "" : "s"} total` : undefined}
        />
      </div>
      {loading && <div className="text-ink-500 text-sm">Loading…</div>}
      {error && <div className="text-red-700 text-sm">Failed to load: {String(error.message)}</div>}
      {data && !data.latest && (
        <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
          No audit run yet. Run <code className="bg-white px-1 rounded text-xs">rankforge audit --site &lt;dir&gt;</code>;
          results auto-persist here on every run (use <code className="bg-white px-1 rounded text-xs">--no-persist</code> to skip).
        </div>
      )}
      {data?.latest && <AuditBody latest={data.latest} previousOk={data.previousOk} />}
      {data?.linkVelocity && <LinkVelocityRow v={data.linkVelocity} />}
    </div>
  );
}

function LinkVelocityRow({ v }: { v: NonNullable<AuditResponse["linkVelocity"]> }) {
  // Each audit counts every <a href> on every page that resolves to a
  // same-site path. Delta vs ~7d ago surfaces whether internal-linking
  // strategy is actually growing — a flat or shrinking number means the
  // operator should add cross-links between hyperlocal pages, blog
  // posts, and service pages.
  const delta = v.deltaWeek;
  const trend =
    delta == null ? "" :
    delta > 0 ? `+${delta} this week` :
    delta < 0 ? `${delta} this week` :
    "no change this week";
  const cls =
    delta == null ? "text-ink-500" :
    delta > 0 ? "text-green-700" :
    delta < 0 ? "text-amber-700" :
    "text-ink-500";
  const avg = v.pageCount && v.pageCount > 0 ? Math.round(v.current / v.pageCount * 10) / 10 : null;
  return (
    <div className="mt-3 pt-3 border-t border-ink-100 text-sm flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-ink-500 text-xs uppercase tracking-wider">Internal links</span>
      <span className="font-display font-bold tabular-nums">{v.current.toLocaleString()}</span>
      {avg != null && <span className="text-xs text-ink-500">~{avg}/page</span>}
      <span className={`text-xs ${cls}`}>{trend || "no baseline yet"}</span>
    </div>
  );
}

function AuditBody({ latest, previousOk }: {
  latest: NonNullable<AuditResponse["latest"]>;
  previousOk: boolean | null;
}) {
  const groups: Record<string, AuditCheck[]> = {};
  for (const c of latest.checks) {
    (groups[c.category] ??= []).push(c);
  }
  const orderedCategories = ["identity", "trust", "compliance", "seo", "content"]
    .filter((cat) => groups[cat]?.length);

  const regressed = previousOk === true && !latest.ok;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <ScStat label="Pass" value={latest.passCount.toLocaleString()} />
        <ScStat label="Warn" value={latest.warnCount.toLocaleString()} accent={latest.warnCount > 0} />
        <ScStat label="Fail" value={latest.errorCount.toLocaleString()} accent={latest.errorCount > 0} />
        <ScStat label="Last run" value={relTime(latest.ranAt)} />
      </div>
      {regressed && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 mb-3 text-sm">
          <strong>Regression:</strong> previous audit passed; current run has {latest.errorCount} fail{latest.errorCount === 1 ? "" : "s"}.
        </div>
      )}
      {(latest.warnCount + latest.errorCount > 0) ? (
        <div className="space-y-3">
          {orderedCategories.map((cat) => {
            const issues = groups[cat]!.filter((c) => c.severity !== "pass");
            if (issues.length === 0) return null;
            return (
              <div key={cat}>
                <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">{cat}</h3>
                <ul className="space-y-1">
                  {issues.map((c, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className={
                        c.severity === "error"
                          ? "inline-block w-12 text-center text-xs font-bold py-0.5 rounded bg-red-100 text-red-800"
                          : "inline-block w-12 text-center text-xs font-bold py-0.5 rounded bg-accent-100 text-accent-700"
                      }>
                        {c.severity === "error" ? "FAIL" : "WARN"}
                      </span>
                      <span className="text-ink-700">
                        {c.page && <span className="text-ink-500 font-mono text-xs mr-1">[{c.page}]</span>}
                        <span className="font-medium">{c.name}</span> — {c.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm">
          ✓ All {latest.passCount} checks passed.
        </div>
      )}
    </>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function SearchConsoleCard({ data, loading, error }: {
  data: ScResponse | undefined;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Search Console (last 30 days)</h2>
        <HeaderMeta lastRefreshed={data?.dataRefreshedAt} fallback="No data yet" cron="@daily-at-8-utc" />
      </div>
      {loading && <div className="text-ink-500 text-sm">Loading…</div>}
      {error && (
        <div className="text-red-700 text-sm">Failed to load: {String(error.message)}</div>
      )}
      {data && data.totals.impressions === 0 && (
        <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
          No SC data yet. Either the property is too new (Google has a 2–3 day pipeline lag),
          or no <code className="bg-white px-1 rounded text-xs">rankforge sc-pull</code> has
          been run yet. Run <code className="bg-white px-1 rounded text-xs">rankforge sc-pull --site &lt;dir&gt;</code> on
          a daily cron to keep this fresh.
        </div>
      )}
      {data && data.totals.impressions > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <ScStat label="Impressions" value={data.totals.impressions.toLocaleString()} />
            <ScStat label="Clicks" value={data.totals.clicks.toLocaleString()} accent={data.totals.clicks > 0} />
            <ScStat
              label="Avg position"
              value={data.totals.avgPosition != null ? data.totals.avgPosition.toFixed(1) : "—"}
            />
            <ScStat label="Keywords" value={data.totals.keywords.toLocaleString()} />
          </div>

          {data.daily.length > 1 && <Sparkline daily={data.daily} />}

          {data.topQueries.length > 0 && (
            <ScTopQueriesTable queries={data.topQueries.slice(0, 15)} />
          )}
        </>
      )}
    </div>
  );
}

function ScTopQueriesTable({ queries }: { queries: ScResponse["topQueries"] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    queries,
    {
      query: (q) => q.keyword,
      impressions: (q) => q.impressions,
      clicks: (q) => q.clicks,
      avgPos: (q) => q.avgPosition,
    },
    { key: "impressions", dir: "desc" },
  );
  return (
    <div className="mt-4">
      <h3 className="text-sm font-semibold text-ink-700 mb-2">Top queries</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <SortHeader label="Query" sortKey="query" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
              <SortHeader label="Impressions" sortKey="impressions" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
              <SortHeader label="Clicks" sortKey="clicks" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
              <SortHeader label="Avg pos" sortKey="avgPos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 pl-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.map((q) => (
              <tr key={q.keyword}>
                <td className="py-1.5 pr-3 text-ink-900">{q.keyword}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{q.impressions.toLocaleString()}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {q.clicks > 0 ? <strong>{q.clicks}</strong> : <span className="text-ink-400">0</span>}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {q.avgPosition != null ? q.avgPosition.toFixed(1) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScStat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-ink-50 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`mt-0.5 text-xl font-display font-bold tabular-nums ${accent ? "text-accent-600" : "text-ink-900"}`}>
        {value}
      </div>
    </div>
  );
}

function Sparkline({ daily }: { daily: ScResponse["daily"] }) {
  // Two simple inline SVG sparklines: impressions (top, primary) +
  // clicks (bottom, accent). Width fixed at 100%; height ~60px each.
  const W = 600;
  const H = 60;
  const maxImp = Math.max(...daily.map((d) => d.impressions), 1);
  const maxClk = Math.max(...daily.map((d) => d.clicks), 1);
  const xStep = daily.length > 1 ? W / (daily.length - 1) : 0;

  const path = (vals: number[], max: number): string => {
    return vals
      .map((v, i) => {
        const x = i * xStep;
        const y = H - (v / max) * H;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SparkBlock label={`Impressions trend (max ${maxImp.toLocaleString()})`} color="text-blue-600" path={path(daily.map((d) => d.impressions), maxImp)} W={W} H={H} />
      <SparkBlock label={`Clicks trend (max ${maxClk.toLocaleString()})`} color="text-accent-600" path={path(daily.map((d) => d.clicks), maxClk)} W={W} H={H} />
    </div>
  );
}

function SparkBlock({ label, color, path, W, H }: { label: string; color: string; path: string; W: number; H: number }) {
  return (
    <div>
      <div className="text-xs text-ink-500 mb-1">{label}</div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={`w-full h-12 ${color}`}>
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function Metadata({ site }: { site: SiteDetail }) {
  const authorLabel = site.authorName
    ? site.authorTitle
      ? `${site.authorName} · ${site.authorTitle}`
      : site.authorName
    : "(operator fallback)";
  const items: Array<[string, string | null]> = [
    ["Tracking phone", site.phoneNumber],
    ["Forwards to", site.phoneForwardTo],
    ["Hosting", [site.hostingProvider, site.hostingProjectId].filter(Boolean).join(" · ") || null],
    ["Template variant", site.templateVariant],
    ["Primary keyword", site.primaryKeyword],
    ["Author", authorLabel],
    ["Blog posts", site.blogPostCount > 0 ? String(site.blogPostCount) : "0 (run blog-write-bulk)"],
    ["GA4 property", site.ga4PropertyId],
    ["GA4 measurement", site.ga4MeasurementId],
    ["Search Console", site.searchConsoleVerified ? "verified domain property" : "not verified"],
    ["Generated", fmtDate(site.generatedAt)],
    ["Deployed", fmtDate(site.deployedAt)],
    ["Content hash", site.contentHash ? site.contentHash.slice(0, 24) + "…" : null],
  ];
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Site info</h2>
      <dl className="space-y-2 text-sm">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="text-ink-500">{k}</dt>
            <dd className={`text-right ${v ? "text-ink-900" : "text-ink-400"} font-mono text-xs break-all max-w-[60%]`}>
              {v ?? "—"}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone: "blue" | "amber" | "green" | "gray" }) {
  const colors: Record<string, string> = {
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-accent-100 text-accent-700",
    green: "bg-green-100 text-green-800",
    gray: "bg-ink-100 text-ink-700",
  };
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs rounded-md font-medium ${colors[tone]}`}>{children}</span>
  );
}

function statusTone(status: string): "blue" | "amber" | "green" | "gray" {
  if (status === "live") return "green";
  if (status === "rented") return "amber";
  if (status === "deploying") return "blue";
  return "gray";
}

function ScoreBadge({ score, isSpam, type }: { score: number; isSpam: boolean; type: string }) {
  if (isSpam) {
    return <span className="inline-flex px-2 py-0.5 text-xs rounded-md font-medium bg-ink-100 text-ink-500">spam · {score}</span>;
  }
  let tone = "bg-ink-100 text-ink-700";
  if (score >= 75) tone = "bg-green-100 text-green-800";
  else if (score >= 50) tone = "bg-accent-100 text-accent-700";
  else if (score >= 25) tone = "bg-blue-100 text-blue-800";
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-bold ${tone}`}>
      {type === "call" ? "📞 " : ""}{score}
    </span>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function IndexingCard({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["site-indexing", siteId],
    queryFn: () => sitesApi.indexing(siteId),
    refetchInterval: 5 * 60_000,
  });
  const [refreshState, setRefreshState] = useState<
    | { status: "idle" }
    | { status: "running"; runUrl: string }
    | { status: "cooldown"; retryAfterSec: number; lastPulledAt: string }
    | { status: "error"; message: string }
  >({ status: "idle" });

  const triggerRefresh = async () => {
    setRefreshState({ status: "running", runUrl: "" });
    try {
      const token = localStorage.getItem("rankforge.dashboard.token")?.trim() ?? "";
      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/dashboard/sites/${encodeURIComponent(siteId)}/index-refresh`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
      if (resp.status === 429 && body.error === "cooldown") {
        setRefreshState({
          status: "cooldown",
          retryAfterSec: Number(body.retryAfterSec ?? 0),
          lastPulledAt: String(body.lastPulledAt ?? ""),
        });
      } else if (!resp.ok) {
        setRefreshState({ status: "error", message: String(body.message ?? body.error ?? `HTTP ${resp.status}`) });
      } else {
        setRefreshState({ status: "running", runUrl: String(body.runUrl ?? "") });
        // Refetch in 90s — that's about when the workflow's index-check
        // step typically completes for ~30 URLs at concurrency 5.
        setTimeout(() => {
          qc.invalidateQueries({ queryKey: ["site-indexing", siteId] });
          setRefreshState({ status: "idle" });
        }, 90_000);
      }
    } catch (err) {
      setRefreshState({ status: "error", message: (err as Error).message });
    }
  };

  // Compute display state.
  const lastSnapshot = q.data?.dataRefreshedAt ? new Date(q.data.dataRefreshedAt) : null;
  const ageHours = lastSnapshot ? (Date.now() - lastSnapshot.getTime()) / 3_600_000 : null;
  const COOLDOWN_HOURS = 4;
  const onCooldown = ageHours != null && ageHours < COOLDOWN_HOURS;
  const cooldownRemaining = onCooldown ? Math.ceil(COOLDOWN_HOURS - ageHours!) : 0;
  const isRunning = refreshState.status === "running";
  const buttonDisabled = isRunning || onCooldown;
  const buttonTitle = isRunning
    ? "Re-pulling SC URL Inspection — this can take 30-90 seconds…"
    : onCooldown
      ? `Last refresh ${ageHours!.toFixed(1)}h ago. Manual refresh limited to once every ${COOLDOWN_HOURS}h. Available in ~${cooldownRemaining}h.`
      : `Manually re-pull Search Console URL Inspection for this site (~30s, $0 — uses your SC quota: 1000 calls/day across all sites). Limit: once every ${COOLDOWN_HOURS}h.`;

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Indexing (Search Console URL Inspection)</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={triggerRefresh}
            disabled={buttonDisabled}
            title={buttonTitle}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md font-medium border transition ${
              buttonDisabled
                ? "bg-ink-50 border-ink-100 text-ink-400 cursor-not-allowed"
                : "bg-white border-ink-200 text-ink-700 hover:bg-ink-50"
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={isRunning ? "animate-spin" : ""}>
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {isRunning ? "Refreshing…" : onCooldown ? `Cooldown (~${cooldownRemaining}h)` : "Refresh"}
          </button>
          <HeaderMeta lastRefreshed={q.data?.dataRefreshedAt} fallback="No data yet" cron="@daily-at-8-utc" />
        </div>
      </div>
      {refreshState.status === "running" && refreshState.runUrl && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-2 text-xs mb-3 flex items-center gap-2">
          <span>Refresh dispatched on GitHub Actions —</span>
          <a href={refreshState.runUrl} target="_blank" rel="noopener noreferrer" className="underline">view run ↗</a>
          <span className="ml-auto italic">Auto-refetches in ~90s.</span>
        </div>
      )}
      {refreshState.status === "cooldown" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2 text-xs mb-3">
          Cooldown: last refresh was {new Date(refreshState.lastPulledAt).toLocaleString()}.
          {" "}Available in ~{Math.ceil(refreshState.retryAfterSec / 3600)}h.
        </div>
      )}
      {refreshState.status === "error" && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-2 text-xs mb-3">
          Refresh failed: {refreshState.message}
        </div>
      )}
      {q.isLoading && <div className="text-ink-500 text-sm">Loading…</div>}
      {q.error && <div className="text-red-700 text-sm">Failed: {String((q.error as Error).message)}</div>}
      {q.data && q.data.stats.total === 0 && (
        <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
          No URL inspection data yet. Run <code className="bg-white px-1 rounded text-xs">rankforge index-check --site &lt;dir&gt;</code> to
          check Google's indexing status for every URL in the sitemap (uses the SC URL Inspection API; ~30s for 30 URLs).
          Schedule weekly via cron.
        </div>
      )}
      {q.data && q.data.stats.total > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <ScStat label="Indexed" value={`${q.data.stats.indexed}/${q.data.stats.total}`} accent={q.data.stats.indexed > 0} />
            <ScStat label="Not indexed" value={String(q.data.stats.notIndexed)} />
            <ScStat label="Excluded" value={String(q.data.stats.excluded)} />
            <ScStat label="Rich results" value={String(q.data.stats.withRichResults)} />
          </div>
          <IndexingProgressBar stats={q.data.stats} />
          <IndexingPagesTable pages={q.data.pages} />
        </>
      )}
    </div>
  );
}

function IndexingPagesTable({ pages }: { pages: IndexingResponse["pages"] }) {
  const visible = pages.slice(0, 25);
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    visible,
    {
      url: (p) => p.url,
      coverage: (p) => p.coverageState,
      crawled: (p) => p.lastCrawled,
    },
  );
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="URL" sortKey="url" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
            <SortHeader label="Coverage" sortKey="coverage" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 px-3" />
            <SortHeader label="Last crawled" sortKey="crawled" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 px-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((p) => <IndexingRow key={p.id} page={p} />)}
        </tbody>
      </table>
      {pages.length > 25 && (
        <p className="text-xs text-ink-500 mt-2">Showing first 25 of {pages.length} pages.</p>
      )}
    </div>
  );
}

function IndexingProgressBar({ stats }: { stats: { indexed: number; notIndexed: number; excluded: number; other: number; total: number } }) {
  const pct = (n: number) => `${(n / stats.total * 100).toFixed(0)}%`;
  return (
    <div className="space-y-2">
      <div className="flex h-3 rounded-md overflow-hidden border border-ink-200">
        {stats.indexed > 0 && <div className="bg-green-500" style={{ width: pct(stats.indexed) }} title={`${stats.indexed} indexed`} />}
        {stats.notIndexed > 0 && <div className="bg-amber-400" style={{ width: pct(stats.notIndexed) }} title={`${stats.notIndexed} not indexed`} />}
        {stats.excluded > 0 && <div className="bg-red-400" style={{ width: pct(stats.excluded) }} title={`${stats.excluded} excluded`} />}
        {stats.other > 0 && <div className="bg-ink-300" style={{ width: pct(stats.other) }} title={`${stats.other} other`} />}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-ink-600">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500"></span>Indexed</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400"></span>Not indexed</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400"></span>Excluded</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-ink-300"></span>Other</span>
      </div>
    </div>
  );
}

function IndexingRow({ page }: { page: IndexingResponse["pages"][number] }) {
  const cov = page.coverageState ?? "—";
  const isIndexed = /indexed/i.test(cov) && !/not\s+indexed/i.test(cov);
  const isExcluded = /excluded/i.test(cov);
  const tone = isIndexed ? "bg-green-100 text-green-800"
    : isExcluded ? "bg-red-100 text-red-800"
    : "bg-amber-100 text-amber-800";
  const path = (() => {
    try {
      const u = new URL(page.url);
      return u.pathname;
    } catch {
      return page.url;
    }
  })();
  return (
    <tr>
      <td className="py-1.5 pr-3 font-mono text-xs text-ink-700">
        <a href={page.url} target="_blank" rel="noreferrer" className="hover:text-accent">{path}</a>
      </td>
      <td className="py-1.5 px-3">
        <span className={`inline-flex px-1.5 py-0.5 text-xs rounded font-medium ${tone}`}>{cov}</span>
      </td>
      <td className="py-1.5 px-3 text-xs text-ink-600">
        {page.lastCrawled ? relTime(page.lastCrawled) : <span className="text-ink-400">—</span>}
      </td>
    </tr>
  );
}

/**
 * Performance card. PSI Lighthouse lab scores (Performance / Accessibility
 * / Best-Practices / SEO 0-100) plus Core Web Vitals from CrUX field data
 * (LCP / INP / CLS — what Google actually ranks on in 2026, replacing the
 * deprecated Mobile-Friendly Test).
 *
 * Design choices:
 * - Mobile + Desktop side-by-side because operators need to compare. Mobile
 *   is what Google primarily ranks on; desktop is the "should be easy"
 *   sanity check.
 * - Lighthouse scores rendered as circular gauges in 4 colors:
 *   green ≥90 (good), amber ≥50 (needs work), red <50 (poor).
 * - CWV section auto-hides when CrUX has no data (which is the case for
 *   every site with low traffic — accurate signal, not a fail).
 * - Per-URL drill-down is collapsible; default-collapsed since most sites
 *   only have homepage data daily and 1 row is enough.
 */
function PerformanceCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["psi", siteId],
    queryFn: () => psiApi.forSite(siteId),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  if (q.isLoading) return null;
  if (q.error) return null;
  if (!q.data) return null;
  const mobile = q.data.runs.find((r) => r.strategy === "mobile");
  const desktop = q.data.runs.find((r) => r.strategy === "desktop");
  if (!mobile && !desktop) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-semibold">Performance — PageSpeed Insights</h2>
          <HeaderMeta lastRefreshed={null} fallback="No data yet" cron="@daily-at-8-utc" />
        </div>
        <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
          No PSI data yet. Run <code className="bg-white px-1 rounded text-xs">rankforge psi-check --site &lt;dir&gt;</code>;
          the daily 08:00 UTC cron runs it automatically once the homepage is reachable.
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-semibold">Performance — PageSpeed Insights</h2>
        <HeaderMeta lastRefreshed={q.data.dataRefreshedAt} fallback="No data yet" cron="@daily-at-8-utc" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PsiPanel label="Mobile" run={mobile} />
        <PsiPanel label="Desktop" run={desktop} />
      </div>
      {q.data.runs.length > 2 && (
        <PsiRunsTable runs={q.data.runs} />
      )}
    </div>
  );
}

function PsiRunsTable({ runs }: { runs: PsiRun[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<PsiRun>(
    runs,
    {
      url: (r) => { try { return new URL(r.url).pathname; } catch { return r.url; } },
      strategy: (r) => r.strategy,
      perf: (r) => r.lighthouse.performance,
      a11y: (r) => r.lighthouse.accessibility,
      best: (r) => r.lighthouse.bestPractices,
      seo: (r) => r.lighthouse.seo,
      cwv: (r) => r.crux.overall,
    },
  );
  return (
    <details className="mt-4 text-xs">
      <summary className="cursor-pointer text-ink-600 hover:text-ink-900">
        All {runs.length} run(s) — per-URL detail
      </summary>
      <table className="w-full mt-2">
        <thead className="text-[10px] uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="URL" sortKey="url" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
            <SortHeader label="Strategy" sortKey="strategy" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 px-2" />
            <SortHeader label="Perf" sortKey="perf" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-2" />
            <SortHeader label="A11y" sortKey="a11y" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-2" />
            <SortHeader label="Best" sortKey="best" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-2" />
            <SortHeader label="SEO" sortKey="seo" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-2" />
            <SortHeader label="CWV" sortKey="cwv" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pl-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((r) => {
            const path = (() => { try { return new URL(r.url).pathname; } catch { return r.url; } })();
            return (
              <tr key={r.url + "|" + r.strategy}>
                <td className="py-1.5 pr-3 font-mono text-ink-700">{path}</td>
                <td className="py-1.5 px-2">{r.strategy}</td>
                <td className="py-1.5 px-2 text-right tabular-nums"><LhSpan v={r.lighthouse.performance} /></td>
                <td className="py-1.5 px-2 text-right tabular-nums"><LhSpan v={r.lighthouse.accessibility} /></td>
                <td className="py-1.5 px-2 text-right tabular-nums"><LhSpan v={r.lighthouse.bestPractices} /></td>
                <td className="py-1.5 px-2 text-right tabular-nums"><LhSpan v={r.lighthouse.seo} /></td>
                <td className="py-1.5 pl-2"><CwvBadge value={r.crux.overall} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

function PsiPanel({ label, run }: { label: string; run: PsiRun | undefined }) {
  if (!run) {
    return (
      <div className="border border-ink-100 rounded-lg p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-semibold text-ink-900">{label}</h3>
          <span className="text-xs text-ink-400">No data</span>
        </div>
      </div>
    );
  }
  const { lighthouse, crux } = run;
  return (
    <div className="border border-ink-100 rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-semibold text-ink-900">{label}</h3>
        <CwvBadge value={crux.overall} />
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <LhGauge label="Perf" value={lighthouse.performance} />
        <LhGauge label="A11y" value={lighthouse.accessibility} />
        <LhGauge label="Best" value={lighthouse.bestPractices} />
        <LhGauge label="SEO" value={lighthouse.seo} />
      </div>
      {crux.hasData ? (
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-ink-100">
          <CwvMetric label="LCP" value={crux.lcpMs != null ? (crux.lcpMs / 1000).toFixed(1) + "s" : "—"} good={crux.lcpMs != null && crux.lcpMs <= 2500} poor={crux.lcpMs != null && crux.lcpMs > 4000} />
          <CwvMetric label="INP" value={crux.inpMs != null ? crux.inpMs + "ms" : "—"} good={crux.inpMs != null && crux.inpMs <= 200} poor={crux.inpMs != null && crux.inpMs > 500} />
          <CwvMetric label="CLS" value={crux.cls != null ? crux.cls.toFixed(2) : "—"} good={crux.cls != null && crux.cls <= 0.1} poor={crux.cls != null && crux.cls > 0.25} />
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-ink-100 text-xs text-ink-500 italic">
          No CrUX field data yet — Google needs ~28 days of real-user traffic before reporting Core Web Vitals. Lab scores above are still useful for catching regressions.
        </div>
      )}
    </div>
  );
}

function LhGauge({ label, value }: { label: string; value: number | null }) {
  const tone = value == null ? "text-ink-400 border-ink-200"
    : value >= 90 ? "text-green-700 border-green-300 bg-green-50"
    : value >= 50 ? "text-amber-700 border-amber-300 bg-amber-50"
    : "text-red-700 border-red-300 bg-red-50";
  return (
    <div className={`border rounded-md p-2 text-center ${tone}`} title={`Lighthouse ${label}: ${value ?? "n/a"}`}>
      <div className="text-lg font-bold tabular-nums leading-none">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function LhSpan({ v }: { v: number | null }) {
  if (v == null) return <span className="text-ink-400">—</span>;
  const tone = v >= 90 ? "text-green-700" : v >= 50 ? "text-amber-700" : "text-red-700";
  return <span className={`font-semibold ${tone}`}>{v}</span>;
}

function CwvMetric({ label, value, good, poor }: { label: string; value: string; good: boolean; poor: boolean }) {
  const tone = good ? "bg-green-50 text-green-800 border-green-200"
    : poor ? "bg-red-50 text-red-800 border-red-200"
    : "bg-amber-50 text-amber-800 border-amber-200";
  return (
    <div className={`border rounded-md p-1.5 text-center ${tone}`}>
      <div className="text-xs font-bold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function CwvBadge({ value }: { value: string | null }) {
  if (!value || value === "no-data") return <span className="text-[11px] text-ink-400 italic">No CrUX data</span>;
  const tone = value === "good" ? "bg-green-100 text-green-800"
    : value === "needs-improvement" ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-800";
  const label = value === "good" ? "CWV good" : value === "needs-improvement" ? "CWV needs work" : "CWV poor";
  return <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${tone}`}>{label}</span>;
}

function TargetKeywordsCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-keywords", siteId],
    queryFn: () => sitesApi.keywords(siteId, 30),
    refetchInterval: 10 * 60_000,
  });
  // Pull DataForSEO probe positions in parallel — leading indicator that
  // shows up immediately even when Search Console hasn't surfaced traffic
  // yet (typical 2-7 day lag for new/recrawled pages).
  const probeQ = useQuery({
    queryKey: ["site-rankings", siteId],
    queryFn: () => rankingsApi.forSite(siteId, 60),
    refetchInterval: 10 * 60_000,
  });
  if (q.isLoading) return null;
  if (q.error || !q.data) return null;

  const rankedTargets = q.data.targets.filter((t) => t.impressions > 0);
  const unrankedTargets = q.data.targets.filter((t) => t.impressions === 0);

  // Map DataForSEO probe positions by keyword for cross-reference. Only
  // counts positions in top 100 (rank-pull's depth=100 sometimes returns
  // beyond, but those aren't actionable).
  const probeByKw = new Map<string, { position: number | null; localPack: boolean }>();
  for (const r of probeQ.data?.latest ?? []) {
    if (r.position != null && r.position <= 100) {
      probeByKw.set(r.keyword.toLowerCase(), { position: r.position, localPack: r.localPack });
    }
  }
  const probeRanked = Array.from(probeByKw.values()).length;
  // How many of our declared targets have a DataForSEO probe rank?
  const targetProbeRanked = q.data.targets.filter(
    (t) => probeByKw.has(t.keyword.toLowerCase()),
  ).length;

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Keywords (last 30d, Search Console)</h2>
        <span className="text-xs text-ink-500">
          {q.data.targetRankedCount} of {q.data.targetCount} targets surfaced in SC · {q.data.surprises.length} surprise
        </span>
      </div>
      {/* DataForSEO leading-indicator banner — explains the gap when SC */}
      {/* shows 0 but DataForSEO probe sees us ranking. Critical context */}
      {/* for newly-deployed sites that haven't accrued SC traffic yet. */}
      <div className="mb-4 px-3 py-2 bg-ink-50 border border-ink-100 rounded-md text-xs text-ink-600">
        <strong className="text-ink-900">DataForSEO probe</strong> (leading indicator, rerun via
        <code className="bg-white px-1 mx-1 rounded">rankforge rank-pull</code>):
        {" "}
        <strong className={targetProbeRanked > 0 ? "text-green-700" : "text-ink-500"}>
          {targetProbeRanked} of {q.data.targetCount} targets ranking
        </strong>
        {" · "}
        {probeRanked} total keywords in top 100.
        {" "}
        <span className="text-ink-500">
          {q.data.targetRankedCount === 0 && targetProbeRanked > 0
            ? "Google has indexed + ranked you, but Search Console hasn't accumulated impressions yet (typical 2-7 day lag)."
            : "Search Console traffic confirms real users are seeing you in SERPs."}
        </span>
      </div>

      {/* Target keywords — ALL declared targets, ranked or not */}
      <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold mt-1 mb-2">
        Target keywords ({q.data.targetCount})
      </h3>
      {rankedTargets.length === 0 && unrankedTargets.length === 0 && (
        <p className="text-sm text-ink-500">No targets defined.</p>
      )}
      {rankedTargets.length > 0 && (
        <KeywordTable rows={rankedTargets} probeByKw={probeByKw} />
      )}
      {unrankedTargets.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-ink-500 hover:text-ink-900">
            {unrankedTargets.length} target{unrankedTargets.length === 1 ? "" : "s"} with no Search Console traffic yet
            {targetProbeRanked > 0 && (
              <span className="text-green-700 ml-1">
                ({targetProbeRanked} of these are ranking per DataForSEO probe)
              </span>
            )}
          </summary>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink-500 pl-3">
            {unrankedTargets.map((t) => {
              const probe = probeByKw.get(t.keyword.toLowerCase());
              return (
                <li key={t.keyword} className="font-mono flex items-baseline gap-2">
                  <span>{t.keyword}</span>
                  {probe && probe.position != null && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        probe.position <= 10 ? "bg-green-100 text-green-800"
                        : probe.position <= 30 ? "bg-amber-100 text-amber-800"
                        : "bg-ink-100 text-ink-700"
                      }`}
                      title="DataForSEO probe position"
                    >
                      probe #{probe.position}{probe.localPack ? " · LP" : ""}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {/* Surprise keywords — ranking but NOT in our target list */}
      {q.data.surprises.length > 0 && (
        <>
          <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold mt-5 mb-2">
            Surprise keywords ({q.data.surprises.length}) — ranking but not in our target list
          </h3>
          <p className="text-xs text-ink-500 mb-2">
            Google found you for these queries on its own. High-impression surprises are signal of
            content opportunity — consider building a dedicated page for top performers.
          </p>
          <KeywordTable rows={q.data.surprises} highlightSurprise probeByKw={probeByKw} />
        </>
      )}
    </div>
  );
}

function KeywordTable({
  rows,
  highlightSurprise,
  probeByKw,
}: {
  rows: KeywordSummary[];
  highlightSurprise?: boolean;
  probeByKw?: Map<string, { position: number | null; localPack: boolean }>;
}) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<KeywordSummary>(
    rows,
    {
      query: (r) => r.keyword,
      scPos: (r) => r.recentPosition ?? r.avgPosition,
      probePos: (r) => probeByKw?.get(r.keyword.toLowerCase())?.position,
      movement: (r) => r.delta,
      impressions: (r) => r.impressions,
      clicks: (r) => r.clicks,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="Query" sortKey="query" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
            <SortHeader label="SC pos" sortKey="scPos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" title="Search Console position (lagging — what Google reports for real user impressions)" />
            <SortHeader label="Probe pos" sortKey="probePos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" title="DataForSEO probe position (leading — synthetic SERP query, no user lag)" />
            <SortHeader label="Movement" sortKey="movement" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
            <SortHeader label="Impressions" sortKey="impressions" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
            <SortHeader label="Clicks" sortKey="clicks" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 pl-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((r) => {
            const probe = probeByKw?.get(r.keyword.toLowerCase());
            return (
              <tr key={r.keyword}>
                <td className={`py-1.5 pr-3 ${highlightSurprise ? "text-accent-700" : "text-ink-900"}`}>
                  {r.keyword}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {r.recentPosition != null ? r.recentPosition.toFixed(1) : (r.avgPosition?.toFixed(1) ?? "—")}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums text-xs">
                  {probe?.position != null ? (
                    <span className={
                      probe.position <= 10 ? "text-green-700 font-bold"
                      : probe.position <= 30 ? "text-amber-700"
                      : "text-ink-500"
                    }>
                      #{probe.position}{probe.localPack ? " · LP" : ""}
                    </span>
                  ) : <span className="text-ink-400">—</span>}
                </td>
                <td className="py-1.5 px-3 text-right text-xs">
                  {r.delta == null ? <span className="text-ink-400">—</span>
                    : r.delta > 1 ? <span className="text-green-700 font-bold">↑ {r.delta.toFixed(1)}</span>
                    : r.delta < -1 ? <span className="text-red-700 font-bold">↓ {Math.abs(r.delta).toFixed(1)}</span>
                    : <span className="text-ink-500">— flat</span>}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {r.clicks > 0 ? <strong>{r.clicks}</strong> : <span className="text-ink-400">0</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Ahrefs's Site Audit findings — what their crawler flagged after our
// CF cdn-cgi false-positive filter is applied. The Ahrefs UI shows
// RAW counts that include /cdn-cgi/l/email-protection URLs Cloudflare
// auto-generates for email obfuscation; those look like 4xx pages to
// crawlers but aren't bugs. We strip them at Audrey tick time and store
// real counts here. This card is the "what's actually wrong" view.
function AhrefsIssuesCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-ahrefs-issues", siteId],
    queryFn: () => sitesApi.ahrefsIssues(siteId),
    refetchInterval: 5 * 60_000,
  });

  if (q.isLoading) return null;
  if (q.error) return null;
  const issues = q.data?.issues ?? [];
  if (issues.length === 0) return null;

  // Group by severity for header counts. Distinguish informational
  // notices that are noise (3xx_redirect, http_to_https_redirect,
  // pages_to_submit_to_indexnow, meta_description_changed,
  // canonical_url_changed, h1_tag_changed, word_count_changed,
  // pages_added_to_sitemaps) from real notices the operator should see.
  const INFORMATIONAL = new Set([
    "3xx_redirect",
    "http_to_https_redirect",
    "pages_to_submit_to_indexnow",
    "meta_description_changed",
    "canonical_url_changed",
    "h1_tag_changed",
    "word_count_changed",
    "pages_added_to_sitemaps",
  ]);
  const actionable = issues.filter((i) => !INFORMATIONAL.has(i.issueType));
  const informational = issues.filter((i) => INFORMATIONAL.has(i.issueType));

  const counts = { error: 0, warning: 0, notice: 0 };
  for (const i of actionable) counts[i.severity] += i.realCount;

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Ahrefs Site Audit findings
        </h2>
        <div className="flex gap-3 text-xs">
          <span><strong className={counts.error > 0 ? "text-red-700" : "text-ink-400"}>{counts.error}</strong> err</span>
          <span><strong className={counts.warning > 0 ? "text-accent-700" : "text-ink-400"}>{counts.warning}</strong> warn</span>
          <span><strong className="text-ink-600">{counts.notice}</strong> notice</span>
        </div>
      </div>
      <p className="text-xs text-ink-500 mb-3">
        Real counts after stripping CF <code>/cdn-cgi/</code> email-protection false positives.
        Ahrefs's own UI may show higher numbers — those include the FPs. Use{" "}
        <a className="underline" href="/integrations">/integrations</a> to push a "Recrawl" after fixes.
      </p>

      {actionable.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
              <th className="py-1.5 pr-3">Sev</th>
              <th className="py-1.5 pr-3">Type</th>
              <th className="py-1.5 pr-3 text-right">Count</th>
              <th className="py-1.5">Sample URLs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {actionable.map((i) => (
              <tr key={i.issueType}>
                <td className="py-1.5 pr-3">
                  {i.severity === "error" && <span className="text-red-700">🔴 err</span>}
                  {i.severity === "warning" && <span className="text-accent-700">🟡 warn</span>}
                  {i.severity === "notice" && <span className="text-ink-500">⚪ notice</span>}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs">{i.issueType}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  <strong>{i.realCount.toLocaleString()}</strong>
                </td>
                <td className="py-1.5 text-xs text-ink-500 truncate max-w-md">
                  {(i.sampleUrls ?? []).slice(0, 2).map((u) => u.replace(/^https?:\/\/[^/]+/, "")).join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {informational.length > 0 && (
        <details className="mt-3 text-xs text-ink-500">
          <summary className="cursor-pointer">
            {informational.length} informational notice{informational.length === 1 ? "" : "s"} (expected churn — change tracking + indexing signals)
          </summary>
          <ul className="mt-2 ml-4 list-disc">
            {informational.map((i) => (
              <li key={i.issueType}>
                <code>{i.issueType}</code> — {i.realCount.toLocaleString()} URL{i.realCount === 1 ? "" : "s"}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function AuditHistoryCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-audit-history", siteId],
    queryFn: () => sitesApi.auditHistory(siteId, 30),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return null;
  if (q.error || !q.data || q.data.runs.length === 0) return null;
  return <AuditHistoryTable runs={q.data.runs} />;
}

function AuditHistoryTable({ runs }: { runs: AuditHistoryRun[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<AuditHistoryRun>(
    runs,
    {
      when: (r) => r.ranAt,
      result: (r) => r.ok ? 0 : 1,
      pass: (r) => r.passCount,
      warn: (r) => r.warnCount,
      fail: (r) => r.errorCount,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Audit history</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <SortHeader label="When" sortKey="when" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 pr-3" />
              <SortHeader label="Result" sortKey="result" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1.5 px-3" />
              <SortHeader label="Pass" sortKey="pass" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
              <SortHeader label="Warn" sortKey="warn" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 px-3" />
              <SortHeader label="Fail" sortKey="fail" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1.5 pl-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.map((r) => <AuditHistoryRow key={r.id} run={r} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditHistoryRow({ run }: { run: AuditHistoryRun }) {
  const tone = run.errorCount > 0 ? "bg-red-100 text-red-800"
    : run.warnCount > 0 ? "bg-accent-100 text-accent-700"
    : "bg-green-100 text-green-800";
  return (
    <tr>
      <td className="py-1.5 pr-3 text-ink-600 whitespace-nowrap text-xs">
        {new Date(run.ranAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
      </td>
      <td className="py-1.5 px-3">
        <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-medium ${tone}`}>
          {run.ok ? "✓ pass" : "✗ fail"}
        </span>
      </td>
      <td className="py-1.5 px-3 text-right tabular-nums">{run.passCount}</td>
      <td className="py-1.5 px-3 text-right tabular-nums">{run.warnCount > 0 ? <strong>{run.warnCount}</strong> : <span className="text-ink-400">0</span>}</td>
      <td className="py-1.5 pl-3 text-right tabular-nums">{run.errorCount > 0 ? <strong className="text-red-700">{run.errorCount}</strong> : <span className="text-ink-400">0</span>}</td>
    </tr>
  );
}

function PressReleaseCard({ site }: { site: SiteDetail }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["site-pr", site.id],
    queryFn: () => sitesApi.pressReleases(site.id),
  });
  const draft = useMutation({
    mutationFn: (input: { type: "launch" | "service-expansion" | "milestone"; neighborhood?: string; milestoneCount?: number }) =>
      sitesApi.draftPr(site.id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-pr", site.id] }),
  });
  const [openType, setOpenType] = useState<"launch" | "service-expansion" | "milestone" | null>(null);
  const [neighborhood, setNeighborhood] = useState("");
  const [milestoneCount, setMilestoneCount] = useState(100);
  const [viewing, setViewing] = useState<PressRelease | null>(null);

  function fire() {
    if (!openType) return;
    draft.mutate({
      type: openType,
      neighborhood: openType === "service-expansion" ? neighborhood : undefined,
      milestoneCount: openType === "milestone" ? milestoneCount : undefined,
    });
    setOpenType(null);
  }

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Press releases</h2>
        <div className="flex gap-2">
          <button onClick={() => setOpenType("launch")}
                  className="text-xs px-2 py-1 bg-ink-900 text-white rounded font-medium hover:bg-ink-800">
            + Launch
          </button>
          <button onClick={() => setOpenType("service-expansion")}
                  className="text-xs px-2 py-1 bg-ink-900 text-white rounded font-medium hover:bg-ink-800">
            + Expansion
          </button>
          <button onClick={() => setOpenType("milestone")}
                  className="text-xs px-2 py-1 bg-ink-900 text-white rounded font-medium hover:bg-ink-800">
            + Milestone
          </button>
        </div>
      </div>

      {draft.isPending && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 mb-3 text-sm">
          Drafting via Anthropic… ~10-20 seconds.
        </div>
      )}
      {draft.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-3 text-sm">
          {String((draft.error as Error).message)}
        </div>
      )}

      {q.isLoading && <div className="text-ink-500 text-sm">Loading…</div>}
      {q.data && q.data.pressReleases.length === 0 && !draft.isPending && (
        <div className="bg-ink-50 border border-ink-100 rounded p-4 text-sm text-ink-600">
          No PR drafts yet. Click + Launch above to generate one. Submit drafts to PRWeb / EIN Presswire ($99-200) for backlinks.
        </div>
      )}
      {q.data && q.data.pressReleases.length > 0 && (
        <div className="space-y-2">
          {q.data.pressReleases.map((pr) => (
            <PressReleaseRow key={pr.id} pr={pr} onView={() => setViewing(pr)}
                             onChange={() => qc.invalidateQueries({ queryKey: ["site-pr", site.id] })} />
          ))}
        </div>
      )}

      {openType && (
        <Modal title={`New ${openType} press release`} onClose={() => setOpenType(null)}>
          <div className="space-y-3">
            {openType === "service-expansion" && (
              <Field label="Neighborhood">
                <input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)}
                       placeholder="e.g. Phillips Creek Ranch"
                       className="w-full px-3 py-2 border-2 border-ink-100 rounded-lg text-sm" />
              </Field>
            )}
            {openType === "milestone" && (
              <Field label="Milestone count">
                <input type="number" min={50} step={50} value={milestoneCount}
                       onChange={(e) => setMilestoneCount(parseInt(e.target.value))}
                       className="w-full px-3 py-2 border-2 border-ink-100 rounded-lg text-sm" />
              </Field>
            )}
            <button onClick={fire}
                    disabled={openType === "service-expansion" && !neighborhood}
                    className="w-full bg-accent hover:bg-accent-600 disabled:bg-accent-100 text-white font-semibold py-2.5 rounded-lg">
              Generate (~10-20s · ~$0.05)
            </button>
          </div>
        </Modal>
      )}

      {viewing && (
        <Modal title={viewing.headline || "Press release"} onClose={() => setViewing(null)} wide>
          <pre className="text-xs whitespace-pre-wrap font-mono bg-ink-50 p-4 rounded-lg max-h-[60vh] overflow-y-auto">
            {viewing.body}
          </pre>
          <p className="text-xs text-ink-500 mt-2">
            Copy + paste into PRWeb / EIN Presswire / 24-7PressRelease. Then mark "submitted" below.
          </p>
        </Modal>
      )}
    </div>
  );
}

function PressReleaseRow({ pr, onView, onChange }: { pr: PressRelease; onView: () => void; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof pressReleasesApi.update>[1]) => pressReleasesApi.update(pr.id, patch),
    onSuccess: onChange,
  });
  const tone: Record<string, string> = {
    drafted: "bg-blue-100 text-blue-800",
    submitted: "bg-accent-100 text-accent-700",
    published: "bg-green-100 text-green-800",
    killed: "bg-ink-100 text-ink-500",
  };
  return (
    <div className="border border-ink-100 rounded-lg p-3 flex items-center gap-3">
      <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-medium ${tone[pr.status] ?? tone.killed}`}>
        {pr.status}
      </span>
      <span className="text-xs text-ink-500 font-mono">{pr.type}</span>
      <span className="font-medium text-ink-900 flex-1 truncate">{pr.headline}</span>
      <span className="text-xs text-ink-500 whitespace-nowrap">
        {new Date(pr.createdAt).toLocaleDateString()}
      </span>
      <button onClick={onView} className="text-xs text-accent hover:text-accent-700 font-medium">View</button>
      <select value={pr.status} onChange={(e) => update.mutate({ status: e.target.value })}
              disabled={update.isPending}
              className="text-xs px-1.5 py-0.5 border border-ink-200 rounded">
        <option value="drafted">drafted</option>
        <option value="submitted">submitted</option>
        <option value="published">published</option>
        <option value="killed">killed</option>
      </select>
    </div>
  );
}

function ModeFlipCard({ site }: { site: SiteDetail }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (mode: "lead_gen" | "branded_lessee") => sitesApi.setMode(site.id, mode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site", site.id] }),
  });
  const isLeadGen = site.mode === "lead_gen";
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Site mode</h2>
      <p className="text-sm text-ink-600 mb-3">
        Currently <strong>{site.mode === "branded_lessee" ? "branded for lessee" : "lead-gen (default)"}</strong>.
        Flipping the mode just toggles the DB flag — you'll need to re-run
        <code className="bg-ink-100 px-1 rounded text-xs mx-1">rankforge generate</code>
        + redeploy for the schema + content positioning to actually rebuild.
      </p>
      <div className="flex gap-2">
        <button onClick={() => m.mutate("lead_gen")}
                disabled={isLeadGen || m.isPending}
                className={`px-3 py-1.5 text-sm rounded-md font-medium ${isLeadGen ? "bg-blue-100 text-blue-800" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
          {isLeadGen ? "✓ Lead-gen" : "Set lead_gen"}
        </button>
        <button onClick={() => m.mutate("branded_lessee")}
                disabled={!isLeadGen || m.isPending}
                className={`px-3 py-1.5 text-sm rounded-md font-medium ${!isLeadGen ? "bg-accent-100 text-accent-700" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
          {!isLeadGen ? "✓ Branded lessee" : "Set branded_lessee"}
        </button>
        {m.isPending && <span className="text-xs text-ink-500 self-center">saving…</span>}
      </div>
      {m.data?.nextStep && (
        <p className="text-xs text-ink-500 mt-2 font-mono bg-ink-50 p-2 rounded">{m.data.nextStep}</p>
      )}
    </div>
  );
}

function TierFlipCard({ site }: { site: SiteDetail }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (tier: 1 | 2 | 3) => sitesApi.setTier(site.id, tier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site", site.id] }),
  });
  const tier = (site.tier ?? 1) as 1 | 2 | 3;
  const labels: Record<1 | 2 | 3, { name: string; desc: string; tone: string }> = {
    1: { name: "T1 Full index", desc: "All pages get index,follow. Default — use for proven markets.", tone: "bg-green-100 text-green-800" },
    2: { name: "T2 Home + services only", desc: "Home + service detail pages indexed; everything else noindex,follow until proven.", tone: "bg-amber-100 text-amber-800" },
    3: { name: "T3 Sandboxed", desc: "Site-wide noindex,follow. Use for new sites with no signals yet — promote to T1 once first impressions/leads arrive.", tone: "bg-ink-200 text-ink-700" },
  };
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Indexing tier</h2>
      <p className="text-sm text-ink-600 mb-3">
        Currently <strong className={`px-2 py-0.5 rounded text-xs ${labels[tier].tone}`}>{labels[tier].name}</strong>.
        {" "}{labels[tier].desc} Per LocalSEOPlaybook §H. Re-run
        <code className="bg-ink-100 px-1 rounded text-xs mx-1">rankforge generate</code>
        + redeploy after changing for the robots meta to update in rendered HTML.
      </p>
      <div className="flex gap-2">
        {([1, 2, 3] as const).map((t) => (
          <button
            key={t}
            onClick={() => m.mutate(t)}
            disabled={tier === t || m.isPending}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${tier === t ? labels[t].tone : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}
          >
            {tier === t ? "✓ " : ""}{labels[t].name}
          </button>
        ))}
        {m.isPending && <span className="text-xs text-ink-500 self-center">saving…</span>}
      </div>
      {m.data?.nextStep && (
        <p className="text-xs text-ink-500 mt-2 font-mono bg-ink-50 p-2 rounded">{m.data.nextStep}</p>
      )}
    </div>
  );
}

function BlogPostsCard({ siteId, domain }: { siteId: string; domain: string }) {
  const q = useQuery({
    queryKey: ["site", siteId, "blog-posts"],
    queryFn: () => sitesApi.blogPosts(siteId),
  });
  const eventsQ = useQuery({
    queryKey: ["site", siteId, "blog-publish-events"],
    queryFn: () => sitesApi.blogPublishEvents(siteId),
  });
  const fmtAgo = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.round(ms / 60_000);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    return `${d}d ago`;
  };
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Blog posts</h2>
      {q.isLoading && <p className="text-sm text-ink-500">Loading…</p>}
      {q.error && <p className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</p>}
      {q.data && q.data.posts.length === 0 && (
        <>
          <p className="text-sm text-ink-700 mb-2">
            <strong>No posts yet.</strong> Live site shows the "Articles coming soon" stub.
          </p>
          <p className="text-xs text-ink-500 font-mono bg-ink-50 p-2 rounded">
            rankforge blog-write-bulk --domain {domain} --posts-per-site 3 --commit
            <br />
            rankforge blog-render --domain {domain} --deploy
          </p>
        </>
      )}
      {q.data && q.data.posts.length > 0 && (
        <>
          <ul className="divide-y divide-ink-100">
            {q.data.posts.map((p) => (
              <li key={p.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-3">
                  <a
                    href={`https://${domain}/blog/${p.slug}.html`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-ink-900 hover:text-accent text-sm"
                  >
                    {p.title}
                  </a>
                  <div className="text-right whitespace-nowrap">
                    {p.datePublished && (
                      <time className="text-xs text-ink-500 tabular-nums block">
                        {p.datePublished}
                      </time>
                    )}
                    {(() => {
                      // Show "Updated Xd ago" when the row's been
                      // modified meaningfully later than its publish
                      // date — e.g. Owen appended an internal link.
                      // Threshold: 24h after createdAt avoids noise from
                      // the initial insert→update sequence at write time.
                      // When the post is in Owen's 7-day cooldown (same
                      // logic Owen's add-internal-link gate uses), badge
                      // it explicitly so the operator knows Owen will
                      // skip this post until the window clears.
                      if (!p.updatedAt) return null;
                      const updatedMs = new Date(p.updatedAt).getTime();
                      const createdMs = new Date(p.createdAt).getTime();
                      const editThresholdMs = 24 * 60 * 60 * 1000;
                      const owenEdited = updatedMs - createdMs >= editThresholdMs;
                      if (!owenEdited) return null;
                      const ageMs = Date.now() - updatedMs;
                      const cooldownMs = 7 * 24 * 60 * 60 * 1000;
                      const inCooldown = ageMs < cooldownMs;
                      const min = Math.round(ageMs / 60_000);
                      let agoLabel;
                      if (min < 60) agoLabel = `${min}m ago`;
                      else if (min < 24 * 60) agoLabel = `${Math.round(min / 60)}h ago`;
                      else agoLabel = `${Math.round(min / (24 * 60))}d ago`;
                      const cooldownDaysLeft = inCooldown
                        ? Math.max(1, Math.ceil((cooldownMs - ageMs) / (24 * 60 * 60 * 1000)))
                        : 0;
                      return (
                        <div className="mt-0.5 flex flex-col items-end gap-0.5">
                          <time
                            className={`text-[10px] tabular-nums block ${inCooldown ? "text-purple-700 font-medium" : "text-amber-700"}`}
                            title={`Last edited: ${new Date(p.updatedAt).toLocaleString()}`}
                          >
                            updated {agoLabel}
                          </time>
                          {inCooldown && (
                            <span
                              className="text-[10px] px-1 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 font-medium uppercase tracking-wide"
                              title={`Owen waits 7 days after editing a blog post before re-mutating it. Spencer needs that long to capture rank-impact data on the prior edit.`}
                            >
                              cooldown · {cooldownDaysLeft}d
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {p.keyword && <p className="text-xs text-ink-500 font-mono mt-1">kw: {p.keyword}</p>}
                {p.excerpt && <p className="text-xs text-ink-600 mt-1 line-clamp-2">{p.excerpt}</p>}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-500 font-mono bg-ink-50 p-2 rounded mt-4">
            rankforge blog-render --domain {domain} --deploy   # ship to live (~$0)
          </p>
        </>
      )}
      {eventsQ.data && eventsQ.data.events.length > 0 && (
        <div className="mt-4 pt-4 border-t border-ink-100">
          <h3 className="text-xs uppercase tracking-wider text-ink-500 font-medium mb-2">Recent publish history</h3>
          <ul className="space-y-1">
            {eventsQ.data.events.slice(0, 5).map((e) => {
              const tone = e.action === "deployed" ? "text-green-700" : e.action === "drafted" ? "text-blue-700" : "text-ink-700";
              const detail = e.action === "deployed" && e.detailsJson?.post_count
                ? `${e.detailsJson.post_count} post(s) live`
                : e.postSlug ?? "site-level event";
              return (
                <li key={e.id} className="text-xs flex justify-between gap-2">
                  <span><span className={`font-medium ${tone}`}>{e.action}</span> · {detail}</span>
                  <time className="text-ink-500 tabular-nums">{fmtAgo(e.occurredAt)}</time>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function AuthorCard({ site }: { site: SiteDetail }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => sitesApi.clearAuthor(site.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site", site.id] }),
  });
  const hasAuthor = !!site.authorName;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Editorial author</h2>
      {hasAuthor ? (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-medium">{site.authorName}</span>
            {site.authorTitle && <span className="text-sm text-ink-500">— {site.authorTitle}</span>}
          </div>
          {site.authorSlug && (
            <p className="text-xs text-ink-500 mb-2 font-mono">/authors/{site.authorSlug}.html</p>
          )}
          {site.authorBio && <p className="text-sm text-ink-700 mb-3">{site.authorBio}</p>}
          <p className="text-xs text-ink-500 mb-3">
            AI-generated per-site persona (Override §9.1 E-E-A-T). Cleared sites fall back to
            <code className="bg-ink-100 px-1 rounded mx-1">operator.yaml</code>'s author.
          </p>
          <button
            onClick={() => {
              if (confirm(`Clear author "${site.authorName}" for ${site.domain}? Site will fall back to operator.yaml until you regenerate.`)) {
                m.mutate();
              }
            }}
            disabled={m.isPending}
            className="px-3 py-1.5 text-sm rounded-md font-medium bg-ink-100 text-ink-700 hover:bg-ink-200 disabled:opacity-50"
          >
            Clear author
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-ink-700 mb-2">
            <strong>(operator fallback)</strong> — this site has no per-site author; bylines and the
            <code className="bg-ink-100 px-1 rounded mx-1">/authors/</code>page use the operator from
            <code className="bg-ink-100 px-1 rounded mx-1">operator.yaml</code>.
          </p>
          <p className="text-xs text-ink-500 font-mono bg-ink-50 p-2 rounded">
            rankforge authors generate --domain {site.domain} --commit
          </p>
        </>
      )}
      {m.isPending && <span className="text-xs text-ink-500 ml-2">saving…</span>}
      {m.data?.nextStep && (
        <p className="text-xs text-ink-500 mt-2 font-mono bg-ink-50 p-2 rounded">{m.data.nextStep}</p>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-ink-900/50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-md"}`}>
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-900 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-ink-500 font-medium mb-1">{label}</span>
      {children}
    </label>
  );
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-12 text-center text-ink-500">Loading…</div>;
}

function HealthCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["health", siteId],
    queryFn: () => healthApi.forSite(siteId),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return null;
  if (q.error) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-4 text-sm text-ink-500">
        Health: <span className="text-red-600">error loading</span>
      </div>
    );
  }
  if (!q.data || q.data.checks.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-4">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display font-bold text-ink-900">Site Health</h3>
          <span className="text-xs text-ink-500">No checks yet — run <code>rankforge health-check --site &lt;dir&gt;</code></span>
        </div>
      </div>
    );
  }
  const { overall, byCategory, checks } = q.data;
  const stoplight =
    overall === "fail" ? "bg-red-500" : overall === "warn" ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${stoplight}`} />
          <h3 className="font-display font-bold text-ink-900">Site Health</h3>
          <span className="text-xs text-ink-500">{checks.length} checks across {Object.keys(byCategory).length} categories</span>
        </div>
        <HeaderMeta lastRefreshed={checks[0]?.checkedAt ?? null} fallback="Never run" cron="@every-15-min" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {Object.entries(byCategory).map(([cat, b]) => {
          const dot = b.worst === "fail" ? "bg-red-500" : b.worst === "warn" ? "bg-amber-500" : "bg-green-500";
          return (
            <div key={cat} className="border border-ink-100 rounded-md p-2 text-xs">
              <div className="flex items-center gap-1.5 font-medium text-ink-800 capitalize">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                {cat}
              </div>
              <div className="text-ink-500 mt-0.5">{b.pass} pass · {b.warn} warn · {b.fail} fail</div>
            </div>
          );
        })}
      </div>
      {checks.some((c) => c.status !== "pass") && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-ink-600 hover:text-ink-900">Show {checks.filter((c) => c.status !== "pass").length} non-pass check(s)</summary>
          <ul className="mt-2 space-y-1">
            {checks.filter((c) => c.status !== "pass").map((c) => (
              <li key={c.id} className="border-l-2 border-ink-200 pl-2">
                <span className={c.status === "fail" ? "text-red-700 font-medium" : "text-amber-700 font-medium"}>
                  {c.status === "fail" ? "✗" : "⚠"} {c.category}/{c.checkName}
                </span>
                {c.remediation && <div className="text-ink-600 mt-0.5">{c.remediation}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function SitemapReportCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["sitemap-report", siteId],
    queryFn: () => sitemapApi.forSite(siteId),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading) return null;
  if (q.error) return null;
  if (!q.data) return null;

  const engines: Array<{ key: "google_sc" | "bing_wmt" | "indexnow"; label: string }> = [
    { key: "google_sc", label: "Google Search Console" },
    { key: "bing_wmt", label: "Bing Webmaster Tools" },
    { key: "indexnow", label: "IndexNow (Bing/Yandex/DDG/Naver)" },
  ];

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display font-bold text-ink-900">Sitemap Submission Report</h3>
        <HeaderMeta
          lastRefreshed={q.data.history[0]?.submittedAt ?? null}
          fallback="No submissions yet"
          cron="@event-driven"
          extra={`${q.data.history.length} submission${q.data.history.length === 1 ? "" : "s"} on record`}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {engines.map((e) => {
          const sub = q.data!.latest[e.key];
          const recovery = q.data!.recovery?.[e.key];
          return (
            <div key={e.key} className="border border-ink-100 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium mb-1">{e.label}</div>
              {sub ? (
                <>
                  <StatusPill status={sub.status} />
                  <div className="text-xs text-ink-600 mt-2">
                    Submitted {new Date(sub.submittedAt).toLocaleString()}
                  </div>
                  {sub.urlCount !== null && (
                    <div className="text-xs text-ink-600 mt-0.5">
                      {sub.urlCount} URLs
                      {sub.indexedCount !== null && ` · ${sub.indexedCount} indexed`}
                    </div>
                  )}
                  {sub.lastCrawledAt && (
                    <div className="text-xs text-ink-500 mt-0.5">
                      Last crawled {new Date(sub.lastCrawledAt).toLocaleDateString()}
                    </div>
                  )}
                  {recovery?.hasOpenAlert && (
                    <DorisStatusLine recovery={recovery} />
                  )}
                </>
              ) : (
                <div className="text-xs text-ink-500 italic">Not submitted yet</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DorisStatusLine({ recovery }: { recovery: NonNullable<SitemapReportResponse["recovery"][keyof SitemapReportResponse["recovery"]]> }) {
  // Three states:
  //   escalated  = Doris gave up after MAX_ATTEMPTS, alert is critical now
  //   pre-first  = no attempts yet, waiting for the initial 6h backoff
  //   between    = N attempts done, waiting for the next backoff window
  const attempts = recovery.attemptsCount;
  if (recovery.escalated) {
    return (
      <div className="mt-2 text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-800">
        <span className="font-semibold">📡 Doris escalated</span>
        <span className="ml-1 text-red-700">— after {attempts} retries; needs your attention</span>
      </div>
    );
  }
  const eta = recovery.nextAttemptInMinutes;
  const etaLabel = eta === null
    ? "—"
    : eta < 60
      ? `${eta}m`
      : eta < 1440
        ? `${Math.round(eta / 60 * 10) / 10}h`
        : `${Math.round(eta / 1440)}d`;
  return (
    <div className="mt-2 text-xs px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800">
      <span className="font-semibold">📡 Doris is on it</span>
      <span className="ml-1 text-blue-700">
        — next retry in {etaLabel}
        {attempts > 0 && ` · ${attempts} prior`}
      </span>
    </div>
  );
}

function StatusPill({ status }: { status: "pending" | "success" | "error" }) {
  const cls =
    status === "success"
      ? "bg-green-100 text-green-700"
      : status === "pending"
        ? "bg-amber-100 text-amber-800"
        : "bg-red-100 text-red-700";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}

// ─── Phase B6: rank tracker card ────────────────────────────────────────
function RankTrackerCard({ siteId, domain }: { siteId: string; domain: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["site-rankings", siteId],
    queryFn: () => rankingsApi.forSite(siteId, 60),
    refetchInterval: 5 * 60_000,
  });
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<RankRow | null>(null);
  const [refreshState, setRefreshState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "cooldown"; retryAfterSec: number; lastPulledAt: string }
    | { status: "error"; message: string }
    | { status: "done"; checked: number; ranked: number }
  >({ status: "idle" });

  const triggerRefresh = async () => {
    setRefreshState({ status: "running" });
    try {
      const token = localStorage.getItem("rankforge.dashboard.token")?.trim() ?? "";
      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/dashboard/sites/${encodeURIComponent(siteId)}/rank-pull-refresh`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
      if (resp.status === 429 && body.error === "cooldown") {
        setRefreshState({
          status: "cooldown",
          retryAfterSec: Number(body.retryAfterSec ?? 0),
          lastPulledAt: String(body.lastPulledAt ?? ""),
        });
      } else if (!resp.ok) {
        setRefreshState({ status: "error", message: String(body.message ?? body.error ?? `HTTP ${resp.status}`) });
      } else {
        setRefreshState({
          status: "done",
          checked: Number(body.keywordsChecked ?? 0),
          ranked: Number(body.keywordsRanked ?? 0),
        });
        qc.invalidateQueries({ queryKey: ["site-rankings", siteId] });
      }
    } catch (err) {
      setRefreshState({ status: "error", message: (err as Error).message });
    }
  };

  // Compute display state for the button + tooltip.
  const lastSnapshot = q.data?.dataRefreshedAt ? new Date(q.data.dataRefreshedAt) : null;
  const ageHours = lastSnapshot ? (Date.now() - lastSnapshot.getTime()) / 3_600_000 : null;
  const COOLDOWN_HOURS = 8;
  const onCooldown = ageHours != null && ageHours < COOLDOWN_HOURS;
  const cooldownRemaining = onCooldown ? Math.ceil(COOLDOWN_HOURS - ageHours!) : 0;
  const isRunning = refreshState.status === "running";
  const buttonDisabled = isRunning || onCooldown;
  const buttonTitle = isRunning
    ? "Refreshing rankings — this can take 30-60 seconds…"
    : onCooldown
      ? `Last refresh ${ageHours!.toFixed(1)}h ago. Manual refresh limited to once every ${COOLDOWN_HOURS}h. Available in ~${cooldownRemaining}h.`
      : `Manually re-pull DataForSEO rankings for this site (~$0.02). Limit: once every ${COOLDOWN_HOURS}h.`;

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
            Rank tracker (DataForSEO)
          </h2>
          <HeaderMeta lastRefreshed={q.data?.dataRefreshedAt ?? null} cron="@every-week" prefix="Snapshot" fallback="never run" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={triggerRefresh}
            disabled={buttonDisabled}
            title={buttonTitle}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md font-medium border transition ${
              buttonDisabled
                ? "bg-ink-50 border-ink-100 text-ink-400 cursor-not-allowed"
                : "bg-white border-ink-200 text-ink-700 hover:bg-ink-50"
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={isRunning ? "animate-spin" : ""}>
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {isRunning ? "Refreshing…" : onCooldown ? `Cooldown (~${cooldownRemaining}h)` : "Refresh"}
          </button>
          <code className="text-xs text-ink-400">rankforge rank-pull --domain &lt;d&gt;</code>
        </div>
      </div>
      {refreshState.status === "cooldown" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2 text-xs mb-3">
          Cooldown: last refresh was {new Date(refreshState.lastPulledAt).toLocaleString()}.
          {" "}Available in ~{Math.ceil(refreshState.retryAfterSec / 3600)}h.
        </div>
      )}
      {refreshState.status === "done" && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-2 text-xs mb-3">
          ✓ Refreshed {refreshState.checked} keyword(s); {refreshState.ranked} ranked in top 100.
        </div>
      )}
      {refreshState.status === "error" && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-2 text-xs mb-3">
          Refresh failed: {refreshState.message}
        </div>
      )}
      {q.isLoading && <div className="text-sm text-ink-500">Loading…</div>}
      {q.error && <div className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</div>}
      {q.data && q.data.latest.length === 0 && (
        <div className="text-sm text-ink-500">
          No rankings snapshots yet. Run{" "}
          <code className="bg-ink-100 px-1 rounded text-xs">rankforge rank-pull --all-from-db</code>
          {" "}weekly to populate.
        </div>
      )}
      {q.data && q.data.latest.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Tile label="Top 3" value={q.data.latest.filter((r) => r.position != null && r.position <= 3).length} tone="green" />
            <Tile label="Top 10" value={q.data.latest.filter((r) => r.position != null && r.position <= 10).length} tone="green" />
            <Tile label="Top 100" value={q.data.latest.filter((r) => r.position != null).length} tone="amber" />
            <Tile label="Local pack" value={q.data.latest.filter((r) => r.localPack).length} tone="green" />
          </div>
          <RankAggregateChart trends={q.data.trends} />
          <RankTrackerTable
            rows={q.data.latest}
            trends={q.data.trends}
            onRowClick={setSelected}
            showAll={showAll}
          />
          {q.data.latest.length > 10 && (
            <button onClick={() => setShowAll((s) => !s)} className="mt-3 text-xs text-accent">
              {showAll ? `Show top 10` : `Show all ${q.data.latest.length} keywords`}
            </button>
          )}
        </>
      )}
      {selected && (
        <KeywordDetailModal
          siteId={siteId}
          domain={domain}
          row={selected}
          trend={q.data?.trends[selected.keyword] ?? []}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/**
 * Aggregate Top-N counts over the 60-day window. Lines for Top-3,
 * Top-10, Top-100. X-axis = day. Each line shows how many keywords
 * fell into that band on that day. Useful for spotting big-picture
 * regressions or growth at a glance.
 */
function RankAggregateChart({ trends }: { trends: Record<string, Array<{ day: string; position: number | null }>> }) {
  // Collect all days across all keywords, sorted ascending.
  const allDays = new Set<string>();
  for (const k of Object.keys(trends)) for (const p of trends[k] ?? []) allDays.add(p.day);
  const days = [...allDays].sort();
  if (days.length < 2) {
    // Need ≥2 snapshots to draw a line. Don't silently disappear —
    // tell the operator why the chart slot is empty so the manual
    // refresh button doesn't feel broken.
    return (
      <div className="mb-4 rounded border border-dashed border-ink-200 bg-ink-50/40 p-3 text-xs text-ink-500">
        <div className="font-medium text-ink-600 mb-0.5">Trend chart waiting on next snapshot</div>
        <div>
          {days.length === 0
            ? "No DataForSEO snapshots yet. Click Refresh above (~$0.02) or wait for the weekly Monday cron."
            : "Only one DataForSEO snapshot on file — the chart needs ≥2 to draw a line. Next data point arrives on the next cron run (Mondays, UTC)."}
        </div>
      </div>
    );
  }

  // For each day, count keywords with position in each band.
  const byDay = new Map<string, { top3: number; top10: number; top100: number }>();
  for (const d of days) byDay.set(d, { top3: 0, top10: 0, top100: 0 });
  for (const k of Object.keys(trends)) {
    for (const p of trends[k] ?? []) {
      if (p.position == null) continue;
      const acc = byDay.get(p.day);
      if (!acc) continue;
      if (p.position <= 100) acc.top100++;
      if (p.position <= 10) acc.top10++;
      if (p.position <= 3) acc.top3++;
    }
  }
  const series = days.map((d) => byDay.get(d)!);
  const max = Math.max(...series.map((s) => s.top100), 1);

  const W = 700;
  const H = 80;
  const xStep = days.length > 1 ? W / (days.length - 1) : 0;
  const path = (vals: number[]): string =>
    vals
      .map((v, i) => `${i === 0 ? "M" : "L"}${(i * xStep).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
      .join(" ");
  const pTop3 = path(series.map((s) => s.top3));
  const pTop10 = path(series.map((s) => s.top10));
  const pTop100 = path(series.map((s) => s.top100));

  return (
    <div className="mb-4">
      <div className="text-xs text-ink-500 mb-1 flex items-center gap-3">
        <span>Aggregate ranking trend ({days.length} days)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-green-700"></span>Top 3</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-blue-600"></span>Top 10</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-amber-600"></span>Top 100</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-20">
        <path d={pTop100} fill="none" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={pTop10} fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={pTop3} fill="none" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/**
 * Click-a-keyword detail modal. Pulls position+impressions+clicks
 * timeseries from /sites/:id/keyword-history (richer than the
 * rankings table's basic trend), renders a position chart with
 * inverted Y axis (#1 is at the TOP), and stat tiles.
 */
function KeywordDetailModal({
  siteId, domain, row, trend, onClose,
}: {
  siteId: string;
  domain: string;
  row: RankRow;
  trend: Array<{ day: string; position: number | null }>;
  onClose: () => void;
}) {
  const historyQ = useQuery({
    queryKey: ["site-keyword-history", siteId, row.keyword, 60],
    queryFn: async () => {
      const r = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/dashboard/sites/${encodeURIComponent(siteId)}/keyword-history?days=60`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("rankforge.dashboard.token")?.trim() ?? ""}` } },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json() as { keywords: Record<string, Array<{ day: string; position: number | null; impressions: number; clicks: number }>> };
      return j.keywords[row.keyword] ?? [];
    },
  });

  // Stats derived from the rankings trend (faster than waiting for history fetch).
  const positions = trend.map((t) => t.position).filter((p): p is number => p != null);
  const best = positions.length > 0 ? Math.min(...positions) : null;
  const worst = positions.length > 0 ? Math.max(...positions) : null;
  const first = positions[0] ?? null;
  const last = positions[positions.length - 1] ?? null;
  const change = first != null && last != null ? last - first : null;
  const daysRanked = positions.length;

  const histRows = historyQ.data ?? [];
  const totalImp = histRows.reduce((s, r) => s + r.impressions, 0);
  const totalClk = histRows.reduce((s, r) => s + r.clicks, 0);

  return (
    <Modal title={row.keyword} onClose={onClose} wide>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Tile label="Current" value={row.position == null ? "—" : `#${row.position}`} tone={row.position != null && row.position <= 10 ? "green" : "amber"} />
        <Tile label="Best (60d)" value={best == null ? "—" : `#${best}`} tone="green" />
        <Tile label="Worst (60d)" value={worst == null ? "—" : `#${worst}`} tone="amber" />
        <Tile
          label="Δ (60d)"
          value={change == null ? "—" : change < 0 ? `▲ ${Math.abs(change)}` : change > 0 ? `▼ ${change}` : "flat"}
          tone={change != null && change < 0 ? "green" : change != null && change > 0 ? "amber" : "amber"}
        />
      </div>
      <PositionChart trend={trend} />
      {row.localPack && (
        <p className="text-sm mb-2">
          <span className="font-medium">Local pack:</span> currently <strong>#{row.localPackPosition ?? "?"}</strong> in the 3-pack.
        </p>
      )}
      {historyQ.isLoading && <p className="text-xs text-ink-500">Loading impressions/clicks…</p>}
      {historyQ.data && histRows.length > 0 && (totalImp > 0 || totalClk > 0) && (
        <div className="mt-4 pt-4 border-t border-ink-100">
          <div className="flex items-baseline gap-4 mb-2">
            <h3 className="text-xs uppercase tracking-wider text-ink-500 font-medium">Search Console</h3>
            <span className="text-xs text-ink-700">{totalImp.toLocaleString()} impressions · {totalClk.toLocaleString()} clicks</span>
          </div>
          <ImpressionsClicksChart history={histRows} />
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-ink-100 text-xs text-ink-500">
        <p>Data source: DataForSEO SERP probe (weekly cadence). {daysRanked} of {trend.length} day(s) ranked in the window.</p>
        <p className="mt-1">Re-pull: <code className="bg-ink-100 px-1 rounded">rankforge rank-pull --domain {domain}</code></p>
      </div>
    </Modal>
  );
}

function PositionChart({ trend }: { trend: Array<{ day: string; position: number | null }> }) {
  if (trend.length < 2) return <p className="text-xs text-ink-500">Need ≥2 data points to draw a trend.</p>;
  const W = 700;
  const H = 180;
  const PAD_TOP = 10;
  const PAD_BOTTOM = 20;
  const PLOT_H = H - PAD_TOP - PAD_BOTTOM;
  // Y axis inverted: position 1 is at the TOP. Cap at 100.
  const ranked = trend.filter((t) => t.position != null);
  const maxPos = Math.min(100, Math.max(...ranked.map((t) => t.position!), 10));
  const xStep = trend.length > 1 ? W / (trend.length - 1) : 0;

  // Build path with breaks for null positions.
  let d = "";
  let prevWasNull = true;
  trend.forEach((t, i) => {
    const x = i * xStep;
    if (t.position == null) {
      prevWasNull = true;
      return;
    }
    const y = PAD_TOP + (Math.min(t.position, maxPos) / maxPos) * PLOT_H;
    d += `${prevWasNull ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)} `;
    prevWasNull = false;
  });

  // Points overlay so each measurement is visible even with gaps.
  const points = trend
    .map((t, i) => ({ ...t, x: i * xStep }))
    .filter((t) => t.position != null);

  // Y gridlines: positions 1, 3, 10, 30, 100 (capped at maxPos)
  const gridLines = [1, 3, 10, 30, 100].filter((p) => p <= maxPos);

  return (
    <div>
      <div className="text-xs text-ink-500 mb-1">Position over time (lower is better)</div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 180 }}>
        {gridLines.map((p) => {
          const y = PAD_TOP + (p / maxPos) * PLOT_H;
          return (
            <g key={p}>
              <line x1={0} x2={W} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={0.5} strokeDasharray="2 3" />
              <text x={4} y={y - 2} fontSize={9} fill="#9ca3af">#{p}</text>
            </g>
          );
        })}
        <path d={d.trim()} fill="none" stroke="#2563eb" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x.toFixed(1)} cy={(PAD_TOP + (Math.min(p.position!, maxPos) / maxPos) * PLOT_H).toFixed(1)} r={3} fill="#2563eb" />
        ))}
        {/* X-axis labels: first + last day */}
        <text x={0} y={H - 5} fontSize={9} fill="#9ca3af">{trend[0]?.day}</text>
        <text x={W - 60} y={H - 5} fontSize={9} fill="#9ca3af">{trend[trend.length - 1]?.day}</text>
      </svg>
    </div>
  );
}

function ImpressionsClicksChart({ history }: { history: Array<{ day: string; impressions: number; clicks: number }> }) {
  if (history.length < 2) return null;
  const W = 700;
  const H = 80;
  const maxImp = Math.max(...history.map((h) => h.impressions), 1);
  const maxClk = Math.max(...history.map((h) => h.clicks), 1);
  const xStep = history.length > 1 ? W / (history.length - 1) : 0;
  const path = (vals: number[], max: number): string =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * xStep).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`).join(" ");
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-xs text-ink-500 mb-1">Impressions (max {maxImp.toLocaleString()})</div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-16">
          <path d={path(history.map((h) => h.impressions), maxImp)} fill="none" stroke="#2563eb" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <div className="text-xs text-ink-500 mb-1">Clicks (max {maxClk.toLocaleString()})</div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-16">
          <path d={path(history.map((h) => h.clicks), maxClk)} fill="none" stroke="#d97706" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

/** Sortable wrapper for the Rank Tracker table. */
function RankTrackerTable({
  rows, trends, onRowClick, showAll,
}: {
  rows: RankRow[];
  trends: Record<string, Array<{ day: string; position: number | null }>>;
  onRowClick: (row: RankRow) => void;
  showAll: boolean;
}) {
  // Position sort treats null (= not ranked) as worst-possible, so
  // ascending = "best ranks first" intuitively. Volume sort does the
  // standard "highest first" via desc default. Trend uses the
  // 60d delta (negative = improving since lower position = better).
  const trendDelta = (kw: string): number | null => {
    const t = trends[kw] ?? [];
    const positions = t.map((p) => p.position).filter((p): p is number => p != null);
    if (positions.length < 2) return null;
    return positions[positions.length - 1]! - positions[0]!;
  };

  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<RankRow>(
    rows,
    {
      keyword: (r) => r.keyword,
      // Position: nulls (n/r) sort as worst — but the hook already pushes
      // nulls to bottom regardless of dir, so just return the number.
      position: (r) => r.position,
      volume: (r) => r.searchVolume,
      // Local pack: true > false; nulls (no presence) sort to bottom.
      localPack: (r) => r.localPack ? (r.localPackPosition ?? 99) : null,
      trend: (r) => trendDelta(r.keyword),
    },
    // No initial sort — preserves Spencer's "ease score" ordering until
    // the operator clicks a column.
  );

  const visible = showAll ? sortedRows : sortedRows.slice(0, 10);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[600px]">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider border-b border-ink-100">
            <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-left" />
            <SortHeader label="Volume" sortKey="volume" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="US monthly search volume from DataForSEO. Higher = more meaningful traffic if you crack the top 10." />
            <SortHeader label="Position" sortKey="position" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Lower number = better rank. Not-ranked rows sort to the bottom in either direction." />
            <SortHeader label="Local pack" sortKey="localPack" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Trend (60d)" sortKey="trend" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Position delta over the last 60 days. Negative = improving (rank getting closer to #1)." />
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <RankRowView
              key={r.keyword}
              row={r}
              trend={trends[r.keyword] ?? []}
              onClick={() => onRowClick(r)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankRowView({ row, trend, onClick }: { row: RankRow; trend: Array<{ day: string; position: number | null }>; onClick?: () => void }) {
  const positions = trend.map((t) => t.position).filter((p): p is number => p != null);
  const trendDelta = positions.length >= 2 ? positions[positions.length - 1]! - positions[0]! : null;
  const positionStr = row.position == null ? "—" : `#${row.position}`;
  const positionTone = row.position == null ? "text-ink-400"
    : row.position <= 3 ? "text-green-700 font-bold"
    : row.position <= 10 ? "text-green-700"
    : row.position <= 30 ? "text-amber-700"
    : "text-ink-500";
  return (
    <tr
      className={`border-b border-ink-50 ${onClick ? "cursor-pointer hover:bg-ink-50/60" : ""}`}
      onClick={onClick}
      title={onClick ? "Click for full keyword report" : undefined}
    >
      <td className="py-2">
        <span className={onClick ? "underline decoration-dotted decoration-ink-300" : ""}>{row.keyword}</span>
      </td>
      <td className="py-2 text-right text-ink-700 tabular-nums">
        {row.searchVolume == null ? <span className="text-ink-400">—</span> : row.searchVolume.toLocaleString()}
      </td>
      <td className={`py-2 text-right ${positionTone}`}>{positionStr}</td>
      <td className="py-2 text-right text-ink-700">{row.localPack ? `#${row.localPackPosition ?? "?"}` : "—"}</td>
      <td className="py-2 text-right text-xs">
        {trendDelta == null ? <span className="text-ink-400">—</span>
          : trendDelta < 0 ? <span className="text-green-700">▲ {Math.abs(trendDelta)}</span>
          : trendDelta > 0 ? <span className="text-red-700">▼ {trendDelta}</span>
          : <span className="text-ink-400">flat</span>}
      </td>
    </tr>
  );
}

// ─── Phase B4: GSC refresh queue card ───────────────────────────────────
function RefreshQueueCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-refresh-queue", siteId],
    queryFn: () => refreshQueueApi.forSite(siteId, 20),
    refetchInterval: 10 * 60_000,
  });
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
            Refresh queue
          </h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Queries with ≥20 impressions but avg position &gt; 10 (last 28d). Highest leverage to refresh.
          </p>
        </div>
      </div>
      {q.isLoading && <div className="text-sm text-ink-500">Loading…</div>}
      {q.error && <div className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</div>}
      {q.data && q.data.items.length === 0 && (
        <div className="text-sm text-ink-500 italic">
          No queries above threshold yet — either GSC hasn't accumulated impressions, or all ranking queries are already in top 10. Either way: good news.
        </div>
      )}
      {q.data && q.data.items.length > 0 && (
        <RefreshQueueTable items={q.data.items.slice(0, 25)} />
      )}
    </div>
  );
}

function RefreshQueueTable({ items }: { items: RefreshQueueItem[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<RefreshQueueItem>(
    items,
    {
      keyword: (it) => it.keyword,
      impressions: (it) => it.impressions,
      clicks: (it) => it.clicks,
      avgPosition: (it) => it.avgPosition,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[480px]">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
            <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <SortHeader label="Impressions" sortKey="impressions" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Clicks" sortKey="clicks" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Avg position" sortKey="avgPosition" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((it) => <RefreshRow key={it.keyword} item={it} />)}
        </tbody>
      </table>
    </div>
  );
}
function RefreshRow({ item }: { item: RefreshQueueItem }) {
  return (
    <tr className="border-b border-ink-50">
      <td className="py-2">{item.keyword}</td>
      <td className="py-2 text-right tabular-nums">{item.impressions.toLocaleString()}</td>
      <td className="py-2 text-right tabular-nums">{item.clicks.toLocaleString()}</td>
      <td className="py-2 text-right tabular-nums text-amber-700">{item.avgPosition.toFixed(1)}</td>
    </tr>
  );
}

function Tile({ label, value, tone }: { label: string; value: number | string; tone: "green" | "amber" | "grey" }) {
  const cls = tone === "green" ? "bg-green-50 border-green-200 text-green-800"
            : tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-ink-50 border-ink-200 text-ink-700";
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-2xl font-display font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function CallsCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-calls", siteId],
    queryFn: () => callsApi.forSite(siteId, 28),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="font-semibold mb-2">📞 Calls (28d)</h2>
        <p className="text-xs text-ink-500">Loading…</p>
      </div>
    );
  }
  if (q.error || !q.data) return null;
  const { tracking, summary, recent } = q.data;

  // No tracking number provisioned — show CTA, not stats.
  if (!tracking?.phoneNumber) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="font-semibold mb-2">📞 Calls (28d)</h2>
        <div className="rounded border border-dashed border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900">
          <div className="font-medium mb-0.5">No tracking number provisioned</div>
          <div className="text-amber-800">
            Call attribution is dark for this site. Provision a Twilio number with:
          </div>
          <code className="block mt-2 bg-white px-2 py-1 rounded text-amber-900 text-[11px]">
            rankforge twilio provision --domain {/* domain not directly available here */}…
          </code>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">📞 Calls (28d)</h2>
        <span className="text-xs text-ink-500">
          tracking: <code className="text-ink-700">{tracking.phoneNumber}</code>
          {tracking.callMode && <> · mode={tracking.callMode}</>}
          {tracking.callMode === "forward" && tracking.phoneForwardTo && <> → {tracking.phoneForwardTo}</>}
          {tracking.provider && <> · {tracking.provider}</>}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <CallStat label="Voicemails" value={summary.voicemails} positive={summary.voicemails > 0} />
        <CallStat label="Calls" value={summary.calls} />
        <CallStat label="Missed" value={summary.missedCalls} warn={summary.missedCalls > summary.completedCalls} />
        <CallStat label="SMS" value={summary.smses} />
      </div>
      {summary.avgDurationSec != null && (
        <p className="text-xs text-ink-500 mb-3">avg call duration: {Math.floor(summary.avgDurationSec / 60)}m {summary.avgDurationSec % 60}s</p>
      )}
      {recent.length === 0 ? (
        <p className="text-xs text-ink-500">No calls or texts yet. Tracking number is live and configured — calls will land here as soon as the first one comes in.</p>
      ) : (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-ink-600 mb-1">Recent</div>
          {recent.map((r) => <CallRow key={r.id} entry={r} />)}
        </div>
      )}
    </div>
  );
}

function CallStat({ label, value, positive, warn }: { label: string; value: number; positive?: boolean; warn?: boolean }) {
  const color = warn ? "text-amber-700" : positive ? "text-green-700" : "text-ink-900";
  return (
    <div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function CallRow({ entry }: { entry: CallEntry }) {
  const isCompleted = entry.type === "call" && (entry.qualityScore ?? 0) >= 60;
  const verb = entry.type === "voicemail" ? "Voicemail"
    : entry.type === "sms" ? "SMS"
    : isCompleted ? "Call" : "Missed call";
  const verbColor = entry.type === "voicemail" ? "text-purple-700"
    : entry.type === "sms" ? "text-blue-700"
    : isCompleted ? "text-green-700" : "text-amber-700";
  const dur = entry.durationSeconds != null && entry.durationSeconds > 0
    ? ` · ${Math.floor(entry.durationSeconds / 60)}m ${entry.durationSeconds % 60}s` : "";
  const when = new Date(entry.receivedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  // Voicemail entries get expanded layout: transcript + audio player.
  if (entry.type === "voicemail") {
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
    const audioSrc = entry.recordingSid
      ? `${apiBase}/webhooks/twilio/recording-proxy?sid=${encodeURIComponent(entry.recordingSid)}`
      : null;
    return (
      <div className="rounded border border-purple-100 bg-purple-50/30 p-2.5 text-xs space-y-1.5">
        <div className="flex items-baseline gap-2">
          <span className={`font-medium ${verbColor}`}>{verb}</span>
          <span className="text-ink-700">{entry.callerPhone ?? "(unknown)"}{dur}</span>
          <span className="text-ink-400 ml-auto whitespace-nowrap">{when}</span>
        </div>
        {entry.transcript ? (
          <div className="text-ink-700 italic">"{entry.transcript}"</div>
        ) : (
          <div className="text-ink-400">Transcribing…</div>
        )}
        {audioSrc && (
          <audio controls preload="none" src={audioSrc} className="w-full h-8" />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-3 text-xs border-b border-ink-50 pb-1.5 last:border-0">
      <span className={`font-medium ${verbColor}`}>{verb}</span>
      <span className="text-ink-700">{entry.callerPhone ?? "(unknown)"}{dur}</span>
      {entry.message && entry.type === "sms" && <span className="text-ink-500 truncate max-w-md">"{entry.message.slice(0, 80)}"</span>}
      <span className="text-ink-400 ml-auto whitespace-nowrap">{when}</span>
    </div>
  );
}

function OffPageCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-ahrefs", siteId],
    queryFn: () => ahrefsApi.forSite(siteId),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading) return null;
  if (q.error) return null;
  if (!q.data || !q.data.latest) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">Off-page (Ahrefs)</h2>
          <span className="text-xs text-ink-500">no data yet</span>
        </div>
        <p className="text-sm text-ink-600">
          Run <code className="bg-ink-100 px-1 rounded text-xs">rankforge ahrefs-pull --domain &lt;d&gt;</code> to populate.
          Cron-daily picks this up automatically once wired.
        </p>
      </div>
    );
  }

  const l = q.data.latest;
  const w = q.data.weekAgo;
  const trend = q.data.trend90d;

  const delta = (now: number | null | undefined, then: number | null | undefined) => {
    if (now == null || then == null) return null;
    return now - then;
  };
  const dDr = delta(l.domainRating, w?.domainRating);
  const dBacklinks = delta(l.backlinksCount, w?.backlinksCount);
  const dRefDom = delta(l.refDomainsCount, w?.refDomainsCount);
  const dTraffic = delta(l.orgTrafficEstimate, w?.orgTrafficEstimate);

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">Off-page (Ahrefs)</h2>
        <span className="text-xs text-ink-500">
          snapshot {new Date(l.snapshotAt).toLocaleString()}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <OffPageStat label="DR" value={l.domainRating ?? 0} delta={dDr} hint="domain rating 0-100" />
        <OffPageStat label="Backlinks" value={l.backlinksCount ?? 0} delta={dBacklinks} />
        <OffPageStat label="Ref domains" value={l.refDomainsCount ?? 0} delta={dRefDom} />
        <OffPageStat label="Org traffic" value={l.orgTrafficEstimate ?? 0} delta={dTraffic} hint="visits / month (est.)" />
      </div>
      {l.spamVerdict && l.spamVerdict !== "clean" && (
        <SpamWarning
          verdict={l.spamVerdict}
          spamCount={l.spamBacklinkCount ?? 0}
          total={l.spamBacklinkSampleSize ?? 0}
          spammyRoots={Array.isArray(l.spammyRoots) ? (l.spammyRoots as string[]) : []}
        />
      )}
      {trend.length > 1 && (
        <div className="text-xs text-ink-500">
          <span className="uppercase tracking-wider font-medium">90-day trend:</span>{" "}
          backlinks {trend[0]?.backlinksCount ?? 0} → {trend[trend.length - 1]?.backlinksCount ?? 0},{" "}
          DR {trend[0]?.domainRating ?? 0} → {trend[trend.length - 1]?.domainRating ?? 0}
        </div>
      )}
    </div>
  );
}

function SpamWarning({
  verdict, spamCount, total, spammyRoots,
}: { verdict: string; spamCount: number; total: number; spammyRoots: string[] }) {
  // mild: amber. spammy: red. Both surface the same shape but diff weight.
  const isSpammy = verdict === "spammy";
  const cls = isSpammy
    ? "bg-red-50 border-red-300 text-red-800"
    : "bg-amber-50 border-amber-300 text-amber-800";
  const icon = isSpammy ? "⚠️" : "⚠";
  const headline = isSpammy
    ? "Spammy backlink history detected"
    : "Some spam in backlink history";
  const guidance = isSpammy
    ? "This domain has a poisoned backlink profile (likely from a prior owner buying link-farm services). DR will decay and Google may penalize. Consider disavowing + checking Search Console Manual Actions."
    : "Some spam-flagged backlinks exist. Review the roots below and consider disavowing.";
  const cmd = `rankforge disavow-generate --domain <this-domain>`;
  return (
    <div className={`mb-4 border rounded-lg p-3 text-sm ${cls}`}>
      <div className="font-semibold mb-1">{icon} {headline}</div>
      <p className="text-xs mb-2">
        {spamCount} of {total} sampled backlinks flagged as spam by Ahrefs.
      </p>
      <p className="text-xs mb-2">{guidance}</p>
      {spammyRoots.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer hover:underline">Spammy root domains ({spammyRoots.length})</summary>
          <ul className="mt-1 ml-4 list-disc">
            {spammyRoots.map((r) => <li key={r} className="font-mono">{r}</li>)}
          </ul>
        </details>
      )}
      <p className="text-xs mt-2">
        Generate a disavow file: <code className="bg-white/50 px-1 rounded">{cmd}</code>
      </p>
    </div>
  );
}

function OffPageStat({
  label, value, delta, hint,
}: { label: string; value: number; delta: number | null; hint?: string }) {
  const cls = delta == null
    ? "text-ink-500"
    : delta > 0
      ? "text-green-700"
      : delta < 0
        ? "text-amber-700"
        : "text-ink-500";
  return (
    <div className="border border-ink-100 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className="text-2xl font-display font-bold tabular-nums mt-1">{value.toLocaleString()}</div>
      <div className={`text-xs ${cls} mt-1`}>
        {delta == null ? "no baseline" :
         delta > 0 ? `+${delta.toLocaleString()} this week` :
         delta < 0 ? `${delta.toLocaleString()} this week` :
         "no change this week"}
      </div>
      {hint && <div className="text-xs text-ink-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function RecentAgentActivityCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-agent-actions", siteId],
    queryFn: () => agentsApi.forSite(siteId),
    refetchInterval: 60_000,
  });
  if (q.isLoading || !q.data) return null;
  if (q.data.actions.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Recent agent activity
        </h2>
        <Link to="/agents" className="text-xs text-ink-500 hover:text-accent">All agents →</Link>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[500px]">
        <tbody>
          {q.data.actions.slice(0, 8).map((a) => (
            <AgentActionRow key={a.id} action={a} />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function AgentActionRow({ action }: { action: SiteAgentAction }) {
  const tone: Record<string, string> = {
    ok: "text-green-700",
    failed: "text-red-700",
    escalated: "text-amber-700",
    dry_run: "text-blue-700",
    skipped: "text-ink-500",
  };
  return (
    <tr className="border-b border-ink-50 last:border-0 align-middle">
      <td className="py-2 pr-3 w-8">
        <Link to={`/agents/${action.agentId}`}>
          <AgentAvatar id={action.agentId} displayName={action.agentDisplayName} emoji={action.agentEmoji} size={28} />
        </Link>
      </td>
      <td className="py-2 pr-3">
        <Link to={`/agents/${action.agentId}`} className="text-ink-900 hover:text-accent text-sm">
          {action.agentDisplayName}
        </Link>
        <div className="text-xs text-ink-500 font-mono">{action.actionKind}</div>
      </td>
      <td className="py-2 pr-3 text-xs">
        <span className={`font-medium ${tone[action.outcome] ?? "text-ink-500"}`}>
          {action.outcome}
        </span>
        {action.dryRun && <span className="ml-1 text-blue-600 text-xs">[dry]</span>}
        {action.errorMessage && (
          <div className="text-xs text-red-700 mt-0.5 max-w-xs truncate" title={action.errorMessage}>
            {action.errorMessage}
          </div>
        )}
      </td>
      <td className="py-2 text-xs text-ink-500 text-right whitespace-nowrap tabular-nums">
        {agentRelTime(action.startedAt)}
      </td>
    </tr>
  );
}

function agentRelTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

// ─── Optimization attempts — per-site Owen → Execute → Grace history ─

function OptimizeAttemptsCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-optimize-attempts", siteId],
    queryFn: () => optimizeApi.siteAttempts(siteId),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading) return null;
  if (!q.data || q.data.attempts.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
            Optimization attempts
          </h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Owen → Execute → Grace cycles for this site, with rank-movement deltas from Spencer's weekly probes.
          </p>
        </div>
        <Link to="/optimize/impact" className="text-xs text-accent hover:underline whitespace-nowrap">
          Portfolio view →
        </Link>
      </div>
      <AttemptsTable rows={q.data.attempts} compact />
      <p className="text-[11px] text-ink-400 mt-3">
        Click "When" → jumps to the inbox thread for that attempt (brief + execution_report + grade messages).
      </p>
    </div>
  );
}

function AttemptsTable({ rows, compact }: { rows: import("../lib/api").OptimizationAttempt[]; compact?: boolean }) {
  // Compute "current per keyword" — only the latest attempt per keyword
  // is on the live page. Earlier ones were redeployed-over (superseded).
  // Rows arrive ordered by attemptedAt DESC, so first occurrence wins.
  const currentByKeyword = new Map<string, string>();
  for (const a of rows) {
    if (!currentByKeyword.has(a.keyword)) currentByKeyword.set(a.keyword, a.id);
  }
  type AttemptRow = import("../lib/api").OptimizationAttempt;
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<AttemptRow>(
    rows,
    {
      keyword: (a) => a.keyword,
      page: (a) => a.targetUrl ?? "",
      pre: (a) => a.prePosition,
      d7: (a) => a.postPositionD7,
      d14: (a) => a.postPositionD14,
      d28: (a) => a.postPositionD28,
      latest: (a) => a.latestPosition,
      delta14: (a) => a.postPositionD14 != null && a.prePosition != null ? a.postPositionD14 - a.prePosition : null,
      grade: (a) => a.gradeScore,
      when: (a) => a.attemptedAt,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
          <tr>
            <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 pr-3" />
            <SortHeader label="Page" sortKey="page" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 pr-3" title="Page that was modified in this attempt. v1 Owen always targets the homepage; per-page briefs (services, blog) will diverge as they ship." />
            <SortHeader label="Pre" sortKey="pre" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Position when Owen briefed this attempt" />
            <SortHeader label="d7" sortKey="d7" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="d14" sortKey="d14" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="d28" sortKey="d28" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="Latest" sortKey="latest" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="Δ 14d" sortKey="delta14" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <th className="text-center py-2 px-3">Status</th>
            <SortHeader label="Grade" sortKey="grade" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            {!compact && <SortHeader label="When" sortKey="when" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-2 pl-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((a) => {
            const delta14 = a.postPositionD14 != null && a.prePosition != null ? a.postPositionD14 - a.prePosition : null;
            const isCurrent = currentByKeyword.get(a.keyword) === a.id;
            // Movement classification — same logic as the server-side
            // version on /optimize/impact, but computed here so the
            // per-site card doesn't need a server change.
            let movement: "awaiting" | "top10_hit" | "improving" | "flat" | "regressing" | "superseded" = "awaiting";
            if (!isCurrent) {
              movement = "superseded";
            } else {
              const anyTop10 = [a.postPositionD7, a.postPositionD14, a.postPositionD28, a.latestPosition].some((p) => p != null && p <= 10);
              if (anyTop10) {
                movement = "top10_hit";
              } else {
                const ageDays = (Date.now() - new Date(a.attemptedAt).getTime()) / (24 * 3600 * 1000);
                if (ageDays >= 7 && a.prePosition != null) {
                  const compareAt = a.postPositionD14 ?? a.postPositionD7 ?? a.latestPosition;
                  if (compareAt != null) {
                    const delta = compareAt - a.prePosition;
                    if (delta <= -2) movement = "improving";
                    else if (delta >= 2) movement = "regressing";
                    else movement = "flat";
                  }
                }
              }
            }
            const rowCls = !isCurrent
              ? "hover:bg-ink-50/40 opacity-60"
              : "hover:bg-ink-50/40";
            const kwCls = "py-2 pr-3 text-ink-700" + (!isCurrent ? " line-through" : "");
            return (
              <tr key={a.id} className={rowCls}>
                <td className={kwCls}>{a.keyword}</td>
                <td className="py-2 pr-3 text-ink-700">
                  {a.targetUrl ? (
                    <a
                      href={a.targetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline hover:text-accent"
                      title={a.targetUrl}
                    >
                      {pageLabel(a.targetUrl)}
                    </a>
                  ) : <span className="text-ink-400">—</span>}
                </td>
                <td className="py-2 px-3 text-center font-mono text-ink-700">{a.prePosition ?? "—"}</td>
                <td className="py-2 px-3 text-center font-mono text-ink-700">{a.postPositionD7 ?? "—"}</td>
                <td className="py-2 px-3 text-center font-mono text-ink-900 font-semibold">{a.postPositionD14 ?? "—"}</td>
                <td className="py-2 px-3 text-center font-mono text-ink-700">{a.postPositionD28 ?? "—"}</td>
                <td className="py-2 px-3 text-center font-mono text-ink-700">{a.latestPosition ?? "—"}</td>
                <td className="py-2 px-3 text-center">
                  {delta14 != null ? (
                    <span className={delta14 < 0 ? "text-green-700 font-semibold" : delta14 > 0 ? "text-red-700 font-semibold" : "text-ink-500"}>
                      {delta14 === 0 ? "0" : delta14 > 0 ? `+${delta14}` : `${delta14}`}
                    </span>
                  ) : <span className="text-ink-400">—</span>}
                </td>
                <td className="py-2 px-3 text-center">
                  <SiteAttemptMovementBadge movement={movement} />
                </td>
                <td className="py-2 px-3 text-center">
                  {a.gradeLetter ? (
                    <span
                      className={`inline-flex items-center justify-center min-w-[24px] h-5 rounded text-[11px] font-bold ${
                        a.gradeLetter === "A" ? "bg-green-600 text-white"
                        : a.gradeLetter === "B" ? "bg-green-500 text-white"
                        : a.gradeLetter === "C" ? "bg-amber-400 text-ink-900"
                        : a.gradeLetter === "D" ? "bg-amber-600 text-white"
                        : "bg-red-600 text-white"
                      }`}
                      title={a.gradeScore != null ? `Score: ${a.gradeScore}%` : undefined}
                    >
                      {a.gradeLetter}
                    </span>
                  ) : <span className="text-ink-400 text-xs">—</span>}
                </td>
                {!compact && (
                  <td className="py-2 pl-3 text-right text-xs text-ink-500 whitespace-nowrap">
                    {a.threadKey ? (
                      <Link to={`/inbox?thread=${encodeURIComponent(a.threadKey)}`} className="text-accent hover:underline">
                        {new Date(a.attemptedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </Link>
                    ) : new Date(a.attemptedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SiteAttemptMovementBadge({ movement }: { movement: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    top10_hit: { cls: "bg-green-100 text-green-800 border-green-200", label: "Top-10 ✓" },
    improving: { cls: "bg-green-50 text-green-700 border-green-200", label: "Improving" },
    flat: { cls: "bg-ink-100 text-ink-700 border-ink-200", label: "Flat" },
    regressing: { cls: "bg-red-50 text-red-700 border-red-200", label: "Regressing" },
    awaiting: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Awaiting" },
    superseded: { cls: "bg-ink-50 text-ink-500 border-ink-200 italic", label: "Superseded" },
  };
  const m = map[movement] ?? map.awaiting!;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
}

// ─── Discovered keywords (Sherlock's reverse-SERP queue) ──────────────

function DiscoveredKeywordsCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-discovered-keywords", siteId],
    queryFn: () => optimizeApi.siteDiscovered(siteId),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading || !q.data || q.data.keywords.length === 0) return null;
  const { counts, keywords } = q.data;
  // Default: show pending only. Toggle reveals probed too.
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
            Discovered keywords
          </h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Sherlock pulled these from this site's top-3 local competitors.{" "}
            <strong>{counts.pending}</strong> pending Spencer's next probe ·{" "}
            <strong>{counts.probed}</strong> already probed ·{" "}
            <strong>{counts.total}</strong> total.
          </p>
        </div>
      </div>
      <DiscoveredKeywordsTable keywords={keywords} />
      <p className="text-[11px] text-ink-400 mt-3">
        Sources:{" "}
        {Object.entries(counts.bySource).slice(0, 5).map(([d, n], i, arr) => (
          <span key={d}>
            <code className="bg-ink-100 px-1 rounded">{d}</code> ({n}){i < arr.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>
    </div>
  );
}

function DiscoveredKeywordsTable({ keywords }: { keywords: DiscoveredKeyword[] }) {
  const visible = keywords.slice(0, 50);
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<DiscoveredKeyword>(
    visible,
    {
      keyword: (k) => k.keyword,
      source: (k) => k.sourceCompetitorDomain,
      volume: (k) => k.estVolume,
      kd: (k) => k.estDifficulty,
      compPos: (k) => k.estCompetitorPosition,
      ourPos: (k) => k.latestPosition,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
          <tr>
            <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 pr-3" />
            <SortHeader label="Source competitor" sortKey="source" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 px-3" />
            <SortHeader label="Volume" sortKey="volume" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="DataForSEO US monthly search volume" />
            <SortHeader label="KD" sortKey="kd" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="0-100 keyword difficulty" />
            <SortHeader label="Comp pos" sortKey="compPos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Competitor's rank for this keyword" />
            <SortHeader label="Our pos" sortKey="ourPos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Our rank if Spencer has probed it yet" />
            <th className="text-center py-2 px-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((k) => (
            <tr key={k.id} className="hover:bg-ink-50/40">
              <td className="py-2 pr-3 text-ink-700">{k.keyword}</td>
              <td className="py-2 px-3 text-xs text-ink-500 truncate max-w-[14em]">{k.sourceCompetitorDomain}</td>
              <td className="py-2 px-3 text-center font-mono text-ink-700">{k.estVolume?.toLocaleString() ?? "—"}</td>
              <td className="py-2 px-3 text-center font-mono text-ink-700">{k.estDifficulty ?? "—"}</td>
              <td className="py-2 px-3 text-center font-mono text-ink-700">{k.estCompetitorPosition ?? "—"}</td>
              <td className="py-2 px-3 text-center font-mono text-ink-900 font-semibold">{k.latestPosition ?? "—"}</td>
              <td className="py-2 px-3 text-center">
                <DiscoveredKeywordStatus k={k} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {keywords.length > 50 && (
        <p className="text-xs text-ink-500 mt-2">Showing first 50 of {keywords.length}.</p>
      )}
    </div>
  );
}

/**
 * Status badge for a discovered keyword. Encodes the row's actual
 * lifecycle state so the operator can see WHY a keyword is sitting
 * where it is — not just "Awaiting probe" but "queue rank 33/1605"
 * (Spencer's drain order) or "Owen-eligible · rank #14" or "Owen
 * cooldown · 7d remaining" (page rewritten recently).
 */
function DiscoveredKeywordStatus({ k }: { k: DiscoveredKeyword }) {
  // Pending: render queue position + ETA. Spencer drains 20/tick at
  // 5-min cadence = 240/hr, so eta_hours ≈ position / 240.
  if (k.status === "pending") {
    const pos = k.queuePosition;
    const total = k.queueTotal;
    let label = "Awaiting probe";
    let detail: string | null = null;
    if (pos != null && total != null) {
      label = `Queue ${pos.toLocaleString()} / ${total.toLocaleString()}`;
      const hours = pos / 240;
      detail = hours < 1
        ? `~${Math.max(1, Math.round(hours * 60))}m`
        : hours < 24
          ? `~${Math.round(hours)}h`
          : `~${Math.round(hours / 24)}d`;
    }
    return (
      <span
        className="inline-flex flex-col items-end gap-0.5"
        title={`Spencer probes the highest-volume pending keywords first. This one is ${pos != null ? `position ${pos} of ${total}` : "queued"} portfolio-wide.`}
      >
        <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide bg-amber-50 text-amber-700 border-amber-200">
          {label}
        </span>
        {detail && <span className="text-[9px] text-ink-500 tabular-nums">eta {detail}</span>}
      </span>
    );
  }
  // Probed: distinguish (a) Owen-eligible (rank 11-30, no cooldown),
  // (b) Owen-cooldown (rank 11-30, page rewritten <14d ago), (c) just
  // probed but rank outside 11-30.
  if (k.status === "probed") {
    const pos = k.latestPosition;
    const inOwenRange = pos != null && pos >= 11 && pos <= 30;
    const cooldownUntilMs = k.pageCooldownUntil ? new Date(k.pageCooldownUntil).getTime() : null;
    const inCooldown = cooldownUntilMs != null && cooldownUntilMs > Date.now();
    if (inOwenRange && !inCooldown) {
      return (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide bg-blue-50 text-blue-700 border-blue-200"
          title={`Rank #${pos} is in the 11-30 push zone. Owen will draft a brief for this on his next tick.`}
        >
          Owen-eligible · #{pos}
        </span>
      );
    }
    if (inOwenRange && inCooldown) {
      const daysLeft = Math.max(1, Math.ceil((cooldownUntilMs! - Date.now()) / (1000 * 60 * 60 * 24)));
      return (
        <span
          className="inline-flex flex-col items-end gap-0.5"
          title={`The homepage was rewritten in the last 14 days. Owen waits before re-mutating it so we can measure the prior change. Cooldown clears ${new Date(cooldownUntilMs!).toLocaleDateString()}.`}
        >
          <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide bg-purple-50 text-purple-700 border-purple-200">
            Page cooldown
          </span>
          <span className="text-[9px] text-ink-500 tabular-nums">{daysLeft}d left · #{pos}</span>
        </span>
      );
    }
    return (
      <span
        className="text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide bg-green-50 text-green-700 border-green-200"
        title={pos != null ? `Probed — we rank #${pos}. Outside Owen's 11-30 push zone.` : "Probed — we don't rank in the top 100."}
      >
        Probed{pos != null ? ` · #${pos}` : " · n/r"}
      </span>
    );
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide bg-ink-50 text-ink-500 border-ink-200">
      {k.status}
    </span>
  );
}

// ─── Competitive Landscape (Spencer's per-site research) ───────────────

function CompetitiveLandscapeCard({ siteId }: { siteId: string }) {
  const q = useQuery({
    queryKey: ["site-competitors", siteId],
    queryFn: () => sitesApi.competitors(siteId),
    refetchInterval: 30_000, // refresh once Spencer ticks weekly
    staleTime: 30_000,
  });
  const [openKw, setOpenKw] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm text-sm text-ink-500">
        Loading competitive landscape…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
        Failed: {String((q.error as Error).message)}
      </div>
    );
  }
  if (!q.data || !q.data.marketStructure) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-1">
          Competitive landscape
        </h2>
        <p className="text-sm text-ink-500">
          {q.data?.message ?? "Spencer hasn't probed this site yet — appears after the next weekly tick."}
        </p>
      </div>
    );
  }

  const data = q.data;
  const ms = data.marketStructure!;
  const dirPct = ms.totalSlots > 0 ? Math.round((ms.directorySlots / ms.totalSlots) * 100) : 0;
  const chainPct = ms.totalSlots > 0 ? Math.round((ms.chainSlots / ms.totalSlots) * 100) : 0;
  const localPct = ms.totalSlots > 0 ? Math.round((ms.localSlots / ms.totalSlots) * 100) : 0;
  const diffColor =
    ms.difficultyLabel === "easier" ? "text-green-700"
    : ms.difficultyLabel === "medium" ? "text-amber-700"
    : ms.difficultyLabel === "harder" ? "text-orange-700"
    : "text-red-700";

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Competitive landscape
        </h2>
        <span className="text-[11px] text-ink-500">
          Last probed {data.lastProbedAt ? agentRelTime(data.lastProbedAt) : "never"} · Spencer 🕵
        </span>
      </div>
      <p className="text-[11px] text-ink-500 mb-4">
        Persistent record of Spencer's per-keyword SERP research. Refreshes weekly. Local incumbents are the actionable layer — directories (yelp/angi/bbb) and national chains (rotorooter/etc) are tracked as backdrop only.
      </p>

      {/* Market structure */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
        <div>
          <div className={`text-2xl font-semibold uppercase tracking-wide ${diffColor}`}>
            {ms.difficultyLabel}
          </div>
          <div className="text-[11px] text-ink-500 uppercase tracking-wide">Difficulty</div>
          <div className="text-[10px] text-ink-400 mt-0.5">score {ms.difficulty.toFixed(1)}</div>
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums">{ms.distinctLocals}</div>
          <div className="text-[11px] text-ink-500 uppercase tracking-wide">Local incumbents</div>
          <div className="text-[10px] text-ink-400 mt-0.5">distinct domains</div>
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums">{dirPct}%</div>
          <div className="text-[11px] text-ink-500 uppercase tracking-wide">Directory</div>
          <div className="text-[10px] text-ink-400 mt-0.5">{ms.directorySlots} of {ms.totalSlots} slots</div>
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums">{chainPct}%</div>
          <div className="text-[11px] text-ink-500 uppercase tracking-wide">National chain</div>
          <div className="text-[10px] text-ink-400 mt-0.5">{ms.chainSlots} of {ms.totalSlots}</div>
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums">{localPct}%</div>
          <div className="text-[11px] text-ink-500 uppercase tracking-wide">Local</div>
          <div className="text-[10px] text-ink-400 mt-0.5">{ms.localSlots} of {ms.totalSlots}</div>
        </div>
      </div>

      {/* Local incumbents leaderboard */}
      {data.localIncumbents.length > 0 && (
        <LocalIncumbentsTable rows={data.localIncumbents} />
      )}

      {/* Per-keyword breakdown */}
      <div>
        <h3 className="text-xs font-display font-bold uppercase tracking-wider text-ink-700 mb-2">
          Per-keyword detail
        </h3>
        <p className="text-[11px] text-ink-500 mb-2">
          Click a keyword to expand its top-10 SERP. Tags: [dir]=directory · [chain]=national franchise · [local]=competitor.
        </p>
        <div className="space-y-1">
          {data.keywords.map((k) => {
            const open = openKw === k.keyword;
            const ourLabel = k.ourPosition != null ? `#${k.ourPosition}` : "not ranked";
            return (
              <div key={k.keyword} className="border border-ink-100 rounded">
                <button
                  onClick={() => setOpenKw(open ? null : k.keyword)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-xs text-left hover:bg-ink-50/50"
                >
                  <span>
                    <span className="text-ink-400 mr-2">{open ? "▾" : "▸"}</span>
                    <span className="font-mono text-ink-800">"{k.keyword}"</span>
                  </span>
                  <span className="text-ink-500 tabular-nums">
                    we rank {ourLabel}
                    {k.localPack && <span className="ml-2 text-purple-700">·LP</span>}
                  </span>
                </button>
                {open && (
                  <div className="px-3 pb-3 pt-1 bg-ink-50/40">
                    <table className="w-full text-[11px]">
                      <tbody>
                        {k.top10.map((c, i) => {
                          const tagColor =
                            c.classification === "directory" ? "text-ink-400"
                            : c.classification === "national_chain" ? "text-amber-700"
                            : "text-ink-700";
                          const tag =
                            c.classification === "directory" ? "[dir]"
                            : c.classification === "national_chain" ? "[chain]"
                            : "[local]";
                          return (
                            <tr key={i}>
                              <td className="py-0.5 text-ink-400 w-6 text-right pr-2 tabular-nums">{i + 1}.</td>
                              <td className={`py-0.5 w-12 ${tagColor}`}>{tag}</td>
                              <td className={`py-0.5 ${c.isUs ? "font-semibold text-green-700" : ""}`}>
                                {c.domain}
                                {c.isUs && <span className="ml-2 text-[10px] uppercase tracking-wider">us</span>}
                              </td>
                              <td className="py-0.5 text-right tabular-nums text-ink-600">
                                {c.domainRating != null ? `DR ${c.domainRating}` : ""}
                              </td>
                              <td className="py-0.5 text-right tabular-nums text-ink-500 pl-2">
                                {c.backlinksCount != null ? `${c.backlinksCount.toLocaleString()} bl` : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LocalIncumbentsTable({ rows }: { rows: import("../lib/api").SiteCompetitorIncumbent[] }) {
  type Row = import("../lib/api").SiteCompetitorIncumbent;
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<Row>(
    rows,
    {
      domain: (l) => l.domain,
      appearances: (l) => l.appearances,
      dr: (l) => l.domainRating,
      backlinks: (l) => l.backlinksCount,
      refDomains: (l) => l.refDomainsCount,
    },
  );
  return (
    <div className="mb-5">
      <h3 className="text-xs font-display font-bold uppercase tracking-wider text-ink-700 mb-2">
        Local incumbents — your real competition
      </h3>
      <p className="text-[11px] text-ink-500 mb-2">
        Ranked by how many of your target keywords each appears in. DR/backlinks from Ahrefs cache.
      </p>
      <table className="w-full text-xs">
        <thead className="text-ink-500 uppercase tracking-wider">
          <tr>
            <SortHeader label="Domain" sortKey="domain" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-1" />
            <SortHeader label="In keywords" sortKey="appearances" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1" />
            <SortHeader label="DR" sortKey="dr" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1" />
            <SortHeader label="Backlinks" sortKey="backlinks" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1" />
            <SortHeader label="Ref domains" sortKey="refDomains" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-1" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {sortedRows.map((l) => (
            <tr key={l.domain} className="hover:bg-ink-50/50">
              <td className="py-1.5">
                <a
                  href={`https://${l.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  {l.domain}
                </a>
              </td>
              <td className="py-1.5 text-right tabular-nums">{l.appearances}/{l.keywordsTotal}</td>
              <td className="py-1.5 text-right tabular-nums">{l.domainRating ?? "—"}</td>
              <td className="py-1.5 text-right tabular-nums">
                {l.backlinksCount != null ? l.backlinksCount.toLocaleString() : "—"}
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {l.refDomainsCount != null ? l.refDomainsCount.toLocaleString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
